import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  ClickAwayListener,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  DateRange as DateRangeIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ComposedChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DownloadFileRow } from '../../components/file/fileVisuals';
import {
  api,
  AdminByDateAnalyticsRow,
  AdminByDateAnalyticsSummary,
  AdminByDateQualifyingActivityProjectDetail,
  AdminByDateQualifyingTransactionDetail,
  AdminByDateQualifyingTransactionWithAgent,
  AdminJyUserRow,
  AgentProfile,
  AgentSoldUnit,
  AnalyticsDashboardResponse,
  AnalyticsProjectStat,
  MaterialDetailItem,
  MaterialEngagementResponse,
  MaterialEngagementSeriesPoint,
  MaterialEngagementTopMaterial,
  MaterialAppSource,
  ProjectAnalyticsResponse,
  ProjectAnalyticsTopUserRow,
  ProjectEoiAgentRow,
  ProjectMaterialAnalyticsResponse,
  ProjectMaterialDailyPoint,
  ProjectMaterialFileRow,
  ProjectMaterialTopDownloaderRow,
  SuccessCloserRow,
} from '../../services/api';
import {
  DateRangeSelector,
  DateRangeState,
  DateRangePreset,
  formatInputDate,
  parseInputDate,
  isBeforeDate,
  formatDisplayRange,
  calendarToday,
  addDaysDate,
  startOfMonthDate,
  DEFAULT_DATE_RANGE_PRESETS,
} from './DateRangeSelector';
import TokenUsageAnalyticsDialog from './TokenUsageAnalyticsDialog';
import CheckUserTab from './CheckUserTab';
import ShareLinksAnalyticsTab from './share-links-analytics/ShareLinksAnalyticsTab';
import MaterialActivityLineChart from './MaterialActivityLineChart';

type DrillDownTab = 'all' | 'eoi';
type DrillDownEoiSortField = 'ceaNumber' | 'name' | 'mobile' | 'viewsPeriod' | 'eoiCount' | 'closeNewLaunchCount';
type ProjectTabSubTab = 'top' | 'eoi';

const MATERIAL_APP_SOURCE_OPTIONS: Array<{ value: MaterialAppSource; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'super_picker', label: 'Super Picker' },
  { value: 'project_essential', label: 'Project Essentials' },
];

type MaterialDailySeriesPoint = ProjectMaterialDailyPoint | MaterialEngagementSeriesPoint;

function dailyEdmPreviews(p: MaterialDailySeriesPoint, source: MaterialAppSource): number {
  if (source === 'super_picker') return p.spEdmPreviews;
  if (source === 'project_essential') return p.peEdmPreviews;
  return p.spEdmPreviews + p.peEdmPreviews;
}

function dailyEdmDownloads(p: MaterialDailySeriesPoint, source: MaterialAppSource): number {
  if (source === 'super_picker') return p.spEdmDownloads;
  if (source === 'project_essential') return p.peEdmDownloads;
  return p.spEdmDownloads + p.peEdmDownloads;
}

function dailyEbookPreviews(p: MaterialDailySeriesPoint, source: MaterialAppSource): number {
  if (source === 'super_picker') return p.spEbookPreviews;
  if (source === 'project_essential') return p.peEbookPreviews;
  return p.spEbookPreviews + p.peEbookPreviews;
}

function dailyEbookDownloads(p: MaterialDailySeriesPoint, source: MaterialAppSource): number {
  if (source === 'super_picker') return p.spEbookDownloads;
  if (source === 'project_essential') return p.peEbookDownloads;
  return p.spEbookDownloads + p.peEbookDownloads;
}

function fileRowPreviews(row: ProjectMaterialFileRow, source: MaterialAppSource): number {
  if (source === 'super_picker') return row.spPreviews;
  if (source === 'project_essential') return row.pePreviews;
  return row.previews;
}

function fileRowDownloads(row: ProjectMaterialFileRow, source: MaterialAppSource): number {
  if (source === 'super_picker') return row.spDownloads;
  if (source === 'project_essential') return row.peDownloads;
  return row.downloads;
}

const materialToggleGroupSx = {
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    fontWeight: 600,
    px: 1.25,
    py: 0.35,
    fontSize: '0.75rem',
    borderColor: 'rgba(var(--color-text-rgb), 0.18)',
    color: 'rgba(var(--color-text-rgb), 0.75)',
    '&.Mui-selected': {
      bgcolor: 'rgba(var(--color-accent-rgb), 0.16)',
      color: 'var(--color-accent)',
      borderColor: 'rgba(var(--color-accent-rgb), 0.35)',
      '&:hover': { bgcolor: 'rgba(var(--color-accent-rgb), 0.22)' },
    },
  },
} as const;

const MaterialFilterGroup: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Typography
      variant="caption"
      sx={{
        color: 'rgba(var(--color-text-rgb), 0.5)',
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        fontSize: '0.65rem',
        lineHeight: 1.2,
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

const MaterialAppSourceToggle: React.FC<{
  value: MaterialAppSource;
  onChange: (value: MaterialAppSource) => void;
}> = ({ value, onChange }) => (
  <ToggleButtonGroup
    value={value}
    exclusive
    onChange={(_, val) => { if (val) onChange(val as MaterialAppSource); }}
    size="small"
    sx={materialToggleGroupSx}
  >
    {MATERIAL_APP_SOURCE_OPTIONS.map((opt) => (
      <ToggleButton key={opt.value} value={opt.value}>{opt.label}</ToggleButton>
    ))}
  </ToggleButtonGroup>
);

const MaterialAnalyticsToolbar: React.FC<{
  title: string;
  materialAppSource: MaterialAppSource;
  onMaterialAppSourceChange: (source: MaterialAppSource) => void;
  onRefresh: () => void;
  metric?: 'previews' | 'downloads';
  onMetricChange?: (metric: 'previews' | 'downloads') => void;
  materialType?: 'edm' | 'ebook';
  onMaterialTypeChange?: (type: 'edm' | 'ebook') => void;
  showSourceToggle?: boolean;
}> = ({
  title,
  materialAppSource,
  onMaterialAppSourceChange,
  onRefresh,
  metric,
  onMetricChange,
  materialType,
  onMaterialTypeChange,
  showSourceToggle = true,
}) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 1.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Button
        variant="outlined"
        size="small"
        startIcon={<RefreshIcon />}
        onClick={onRefresh}
        sx={{
          flexShrink: 0,
          borderColor: 'rgba(var(--color-text-rgb), 0.18)',
          color: 'rgba(var(--color-text-rgb), 0.88)',
        }}
      >
        Refresh
      </Button>
    </Box>
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        gap: 2,
        p: 1.25,
        borderRadius: 1,
        border: '1px solid rgba(var(--color-text-rgb), 0.1)',
        bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
      }}
    >
      {showSourceToggle ? (
      <MaterialFilterGroup label="Source">
        <MaterialAppSourceToggle value={materialAppSource} onChange={onMaterialAppSourceChange} />
      </MaterialFilterGroup>
      ) : null}
      {metric && onMetricChange ? (
        <MaterialFilterGroup label="Activity">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={metric}
            onChange={(_, val) => { if (val) onMetricChange(val); }}
            sx={materialToggleGroupSx}
          >
            <ToggleButton value="previews">Previews</ToggleButton>
            <ToggleButton value="downloads">Downloads</ToggleButton>
          </ToggleButtonGroup>
        </MaterialFilterGroup>
      ) : null}
      {materialType && onMaterialTypeChange ? (
        <MaterialFilterGroup label="Material">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={materialType}
            onChange={(_, val) => { if (val) onMaterialTypeChange(val); }}
            sx={materialToggleGroupSx}
          >
            <ToggleButton value="edm">EDM</ToggleButton>
            <ToggleButton value="ebook">eBook</ToggleButton>
          </ToggleButtonGroup>
        </MaterialFilterGroup>
      ) : null}
    </Box>
  </Box>
);

/** Sort keys for browsing / drill-down user tables */
type ProjectTabSortField =
  | 'ceaNumber'
  | 'name'
  | 'mobile'
  | 'viewsAllTime'
  | 'viewsPeriod'
  | 'soldAllTime'
  | 'soldPeriod'
  | 'closeNewLaunchCount';
type SortDirection = 'asc' | 'desc';

const metricHeaderCheckboxSx = {
  p: 0.35,
  color: 'rgba(var(--color-text-rgb), 0.5)',
  '&.Mui-checked': { color: 'var(--color-accent)' },
} as const;

// Splits a metric header label so anything from the first "(" onward wraps to
// the next line, e.g. "Views (all time)" -> head "Views", paren "(all time)".
const splitMetricLabel = (label: string): { head: string; paren: string } => {
  const idx = label.indexOf('(');
  if (idx === -1) {
    return { head: label, paren: '' };
  }
  return { head: label.slice(0, idx).trimEnd(), paren: label.slice(idx) };
};

interface AnalyticsMetricTableHeadCellProps {
  field: ProjectTabSortField;
  label: string;
  hideZero: boolean;
  onHideZeroChange: (next: boolean) => void;
  sortField: ProjectTabSortField;
  sortDir: SortDirection;
  onSort: (field: ProjectTabSortField) => void;
}

const AnalyticsMetricTableHeadCell: React.FC<AnalyticsMetricTableHeadCellProps> = ({
  field,
  label,
  hideZero,
  onHideZeroChange,
  sortField,
  sortDir,
  onSort,
}) => (
  <TableCell
    align="right"
    sx={{
      fontWeight: 600,
      color: 'rgba(var(--color-text-rgb), 0.85)',
      verticalAlign: 'middle',
      whiteSpace: 'nowrap',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}>
      <Checkbox
        size="small"
        checked={hideZero}
        onChange={(e) => onHideZeroChange(e.target.checked)}
        onClick={(e) => e.stopPropagation()}
        sx={metricHeaderCheckboxSx}
      />
      <Box
        component="span"
        onClick={() => onSort(field)}
        sx={{
          cursor: 'pointer',
          userSelect: 'none',
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          lineHeight: 1.2,
          '&:hover': { color: 'var(--color-text-strong)' },
        }}
      >
        {(() => {
          const { head, paren } = splitMetricLabel(label);
          return (
            <>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                {head}
                {sortField === field && (sortDir === 'asc'
                  ? <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                  : <ArrowDownwardIcon sx={{ fontSize: 14 }} />)}
              </Box>
              {paren && <Box component="span">{paren}</Box>}
            </>
          );
        })()}
      </Box>
    </Box>
  </TableCell>
);

interface CloseNewLaunchTableHeadCellProps {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  sortField: ProjectTabSortField | DrillDownEoiSortField;
  sortDir: SortDirection;
  onSort: () => void;
}

const CloseNewLaunchTableHeadCell: React.FC<CloseNewLaunchTableHeadCellProps> = ({
  checked,
  onCheckedChange,
  sortField,
  sortDir,
  onSort,
}) => (
  <TableCell
    align="right"
    sx={{
      fontWeight: 600,
      color: 'rgba(var(--color-text-rgb), 0.85)',
      verticalAlign: 'middle',
      whiteSpace: 'nowrap',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}>
      <Checkbox
        size="small"
        checked={checked}
        onChange={(e) => onCheckedChange(e.target.checked)}
        onClick={(e) => e.stopPropagation()}
        sx={metricHeaderCheckboxSx}
      />
      <Box
        component="span"
        onClick={onSort}
        sx={{
          cursor: 'pointer',
          userSelect: 'none',
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          lineHeight: 1.2,
          '&:hover': { color: 'var(--color-text-strong)' },
        }}
      >
        {(() => {
          const { head, paren } = splitMetricLabel(CLOSE_NEW_LAUNCH_LABEL);
          return (
            <>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                {head}
                {sortField === 'closeNewLaunchCount' && (sortDir === 'asc'
                  ? <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                  : <ArrowDownwardIcon sx={{ fontSize: 14 }} />)}
              </Box>
              {paren && <Box component="span">{paren}</Box>}
            </>
          );
        })()}
      </Box>
    </Box>
  </TableCell>
);

const RESALE_AGENT_INFO_TEXT =
  'Last 12 months, >50K total GCE with at least 85% from resale closing.';

interface ResaleAgentTableHeadCellProps {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
}

const ResaleAgentTableHeadCell: React.FC<ResaleAgentTableHeadCellProps> = ({
  checked,
  onCheckedChange,
}) => {
  const [infoOpen, setInfoOpen] = React.useState(false);
  return (
    <TableCell
      align="right"
      sx={{
        fontWeight: 600,
        color: 'rgba(var(--color-text-rgb), 0.85)',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}>
        <Checkbox
          size="small"
          checked={checked}
          onChange={(e) => onCheckedChange(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          sx={metricHeaderCheckboxSx}
        />
        <ClickAwayListener onClickAway={() => setInfoOpen(false)}>
          <Tooltip
            arrow
            placement="top"
            title={RESALE_AGENT_INFO_TEXT}
            open={infoOpen}
            onClose={() => setInfoOpen(false)}
            disableFocusListener
            disableHoverListener
            disableTouchListener
          >
            <Box
              component="span"
              onClick={() => setInfoOpen((v) => !v)}
              sx={{
                cursor: 'pointer',
                userSelect: 'none',
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                lineHeight: 1.2,
                '&:hover': { color: 'var(--color-text-strong)' },
              }}
            >
              <Box component="span">Resale Agent</Box>
              <Box component="span">(JY rule)</Box>
            </Box>
          </Tooltip>
        </ClickAwayListener>
      </Box>
    </TableCell>
  );
};

interface AnalyticsDashboardDialogProps {
  open: boolean;
  onClose: () => void;
}

const CHART_COLORS = ['#ED8936', '#F6AD55', '#38B2AC', '#4299E1', '#ECC94B', '#F6E05E', '#63B3ED'];

const SOLD_COLUMN_RATE_LIMIT_MESSAGE = '(API LIMIT REACH, Try again in 1 min)';
const CLOSE_NEW_LAUNCH_LABEL = 'No Close NL(03/25-03/26)';

type AnalyticsTab = 'overview' | 'user' | 'project' | 'byDate' | 'successCloser' | 'shareLinks' | 'tokenUsage' | 'jyUsers' | 'checkUser';
/** Bucket size for the Overview tab's time series — `day` keeps the existing daily/hourly behaviour. */
type OverviewGranularity = 'day' | 'month';
type JyUsersSortField = 'name' | 'ceaNumber' | 'mobile' | 'resalePercent' | 'activityCount';
type ByDateResultTab =
  | 'activityAndTransaction'
  | 'noActivityNoTransaction'
  | 'activityNoTransaction'
  | 'noActivityTransaction';
type ByDateSortField =
  | 'name'
  | 'ceaNumber'
  | 'mobile'
  | 'email'
  | 'activityCount'
  | 'transactionCount'
  | 'qualifyingTransactionCount'
  | 'qualifyingActivityCount';

type SoldUnitsRangeMode = 'allTime' | 'selectedPeriod';
type ByDateDrillContext = {
  ceaNumber: string;
  name: string;
};

const chartCardSx = {
  p: 2.5,
  bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
  border: '1px solid rgba(var(--color-text-rgb), 0.08)',
  borderRadius: 2,
  height: '100%',
};

const formatCount = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '-';
  }
  return value.toLocaleString();
};

const MONTH_ABBREVIATIONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Series labels arrive as `YYYY-MM` (monthly), `YYYY-MM-DD` (daily) or `MM/DD HH:00` (hourly).
 * Monthly labels read better as `Aug 2026`; everything else passes through unchanged.
 */
const formatSeriesLabel = (label: string | number | null | undefined): string => {
  const text = String(label ?? '');
  const match = /^(\d{4})-(\d{2})$/.exec(text);
  if (!match) {
    return text;
  }
  const month = MONTH_ABBREVIATIONS[Number(match[2]) - 1];
  return month ? `${month} ${match[1]}` : text;
};

type AnalyticsChartTooltipProps = {
  active?: boolean;
  label?: string | number;
  payload?: Array<{
    value?: number | string | null;
    color?: string;
    name?: string;
  }>;
};

const analyticsBarTooltipSx = {
  px: 1.5,
  py: 1,
  borderRadius: 1.5,
  bgcolor: 'rgba(20, 28, 43, 0.96)',
  border: '1px solid rgba(255,255,255,0.1)',
  boxShadow: '0 12px 28px rgba(0,0,0,0.3)',
};

const AnalyticsChartTooltip: React.FC<AnalyticsChartTooltipProps> = ({ active, label, payload }) => {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <Paper elevation={0} sx={analyticsBarTooltipSx}>
      <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.72)' }}>
        Date
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--color-text-inverse)' }}>
        {formatSeriesLabel(label) || '-'}
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 1 }}>
        {payload.map((entry, index) => {
          const rawValue = entry?.value;
          const count = typeof rawValue === 'number' ? rawValue : Number(rawValue || 0);
          return (
            <Box key={`${entry?.name || 'value'}-${index}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="caption" sx={{ color: entry?.color || 'rgba(255,255,255,0.72)' }}>
                {entry?.name || 'Count'}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: entry?.color || '#63B3ED' }}>
                {formatCount(Number.isFinite(count) ? count : 0)}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

type NormalizedTransactionRole = 'hyper' | 'tagger' | '';
type AgentSoldUnitWithTransactionRole = AgentSoldUnit & { transactionRole: string };

function normalizeTransactionRole(agentRole: string | null | undefined): NormalizedTransactionRole {
  const normalized = (agentRole ?? '').trim().replace(/[\s_-]+/g, '').toLowerCase();
  if (!normalized) {
    return '';
  }
  if (normalized === 'agentmainentity' || normalized === 'mainentity' || normalized === 'hyper') {
    return 'hyper';
  }
  if (normalized === 'agentsubentity' || normalized === 'subentity' || normalized === 'tagger') {
    return 'tagger';
  }
  if (normalized.includes('hyper') || normalized.includes('main')) {
    return 'hyper';
  }
  if (normalized.includes('tagger') || normalized.includes('tag') || normalized.includes('sub')) {
    return 'tagger';
  }
  return '';
}

function transactionRoleRank(agentRole: string | null | undefined): number {
  const role = normalizeTransactionRole(agentRole);
  if (role === 'hyper') return 2;
  if (role === 'tagger') return 1;
  return 0;
}

/** Secure-transactions API role → admin dashboard label (Success Closer / Sold Units). */
function formatAgentRoleLabel(agentRole: string | null | undefined): string {
  const role = normalizeTransactionRole(agentRole);
  if (role === 'hyper') return 'Hyper';
  if (role === 'tagger') return 'Tagger';
  return '—';
}

function normalizeTransactionKeyPart(value: string | number | null | undefined): string {
  return String(value ?? '').trim().replace(/^#/, '').trim().toUpperCase();
}

function normalizeCeaKey(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase();
}

function filterSoldUnitsForCea(units: AgentSoldUnit[], ceaNumber: string | null | undefined): AgentSoldUnit[] {
  const targetCea = normalizeCeaKey(ceaNumber);
  if (!targetCea) {
    return units;
  }
  return units.filter((unit) => {
    const unitCea = normalizeCeaKey(unit.agentCEANo);
    return !unitCea || unitCea === targetCea;
  });
}

function isGenericTransactionNo(transactionNo: string | null | undefined): boolean {
  const value = normalizeTransactionKeyPart(transactionNo);
  return !value || value === 'MASTERSHEET' || value === 'INDEXSHEET';
}

function buildSoldUnitTransactionKey(unit: AgentSoldUnit): string {
  const projectKey = normalizeTransactionKeyPart(unit.reference || unit.projectCode);
  const unitKey = normalizeTransactionKeyPart(unit.unitNo);
  const optionDateKey = normalizeTransactionKeyPart(unit.optionDate);
  const transactionNoKey = normalizeTransactionKeyPart(unit.transactionNo);

  if (projectKey && unitKey && optionDateKey) {
    return `unit|${projectKey}|${unitKey}|${optionDateKey}`;
  }
  if (transactionNoKey && !isGenericTransactionNo(transactionNoKey)) {
    return `tx|${transactionNoKey}`;
  }
  return `fallback|${projectKey}|${unitKey}|${optionDateKey}|${normalizeTransactionKeyPart(unit.transactionPrice)}|${transactionNoKey}`;
}

function withTransactionRole(unit: AgentSoldUnit): AgentSoldUnitWithTransactionRole {
  return {
    ...unit,
    transactionRole: formatAgentRoleLabel(unit.agentRole),
  };
}

function mergeSoldUnitTransaction(
  existing: AgentSoldUnitWithTransactionRole,
  incoming: AgentSoldUnitWithTransactionRole,
): AgentSoldUnitWithTransactionRole {
  const existingRank = transactionRoleRank(existing.agentRole);
  const incomingRank = transactionRoleRank(incoming.agentRole);
  const primary = incomingRank > existingRank ? incoming : existing;
  const secondary = primary === existing ? incoming : existing;

  return {
    ...primary,
    transactionNo: isGenericTransactionNo(primary.transactionNo) && !isGenericTransactionNo(secondary.transactionNo)
      ? secondary.transactionNo
      : primary.transactionNo,
    reference: primary.reference || secondary.reference,
    propertyType: primary.propertyType || secondary.propertyType,
    saleType: primary.saleType || secondary.saleType,
    optionDate: primary.optionDate || secondary.optionDate,
    transactionPrice: primary.transactionPrice || secondary.transactionPrice,
    address: primary.address || secondary.address,
    unitNo: primary.unitNo || secondary.unitNo,
    projectCode: primary.projectCode || secondary.projectCode,
    status: primary.status || secondary.status,
    agentName: primary.agentName || secondary.agentName,
    agentCEANo: primary.agentCEANo || secondary.agentCEANo,
    transactionRole: formatAgentRoleLabel(primary.agentRole),
  };
}

function dedupeSoldUnitTransactions(units: AgentSoldUnit[]): AgentSoldUnitWithTransactionRole[] {
  const byTransaction = new Map<string, AgentSoldUnitWithTransactionRole>();
  for (const unit of units) {
    const key = buildSoldUnitTransactionKey(unit);
    const next = withTransactionRole(unit);
    const existing = byTransaction.get(key);
    byTransaction.set(key, existing ? mergeSoldUnitTransaction(existing, next) : next);
  }
  return Array.from(byTransaction.values());
}

const formatProjectLabel = (projectName: string): string => (
  projectName.length > 28 ? `${projectName.slice(0, 28)}...` : projectName
);

/** Analytics "all time" lower bound: 1 Mar 2026 (DD/MM). */
const ANALYTICS_ALL_TIME_START = '2026-03-01';

const EMPTY_BY_DATE_SUMMARY: AdminByDateAnalyticsSummary = {
  uniqueTransactionCount: 0,
  uniqueTransactedAmount: 0,
  convertedTransactionCount: 0,
  convertedTransactedAmount: 0,
  convertedActivityCount: 0,
  convertedAgentCount: 0,
};

function createAllTimeRange(): DateRangeState {
  const today = calendarToday();
  const start = parseInputDate(ANALYTICS_ALL_TIME_START);
  const end =
    start && isBeforeDate(today, start) ? start : today;
  return {
    startDate: ANALYTICS_ALL_TIME_START,
    endDate: formatInputDate(end),
  };
}

/** Analytics-specific preset list. The shared common presets ("Today",
 * "Yesterday", … "Last month") come from `DEFAULT_DATE_RANGE_PRESETS`;
 * we extend them with the bespoke "All time" entry that snaps to the
 * March 2026 lower bound. */
const ANALYTICS_DATE_PRESETS: DateRangePreset[] = [
  ...DEFAULT_DATE_RANGE_PRESETS,
  { id: 'allTime', label: 'All time', build: () => createAllTimeRange() },
];

const TopProjectsChartHeader: React.FC = () => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
      Top projects by material interactions
    </Typography>
  </Box>
);

const ProjectMaterialsFileList: React.FC<{
  title: string;
  rows: ProjectMaterialFileRow[];
  materialAppSource: MaterialAppSource;
}> = ({ title, rows, materialAppSource }) => {
  const [detailMap, setDetailMap] = React.useState<Record<string, MaterialDetailItem>>({});
  const [detailsLoading, setDetailsLoading] = React.useState(false);

  React.useEffect(() => {
    if (rows.length === 0) { setDetailMap({}); return; }
    setDetailsLoading(true);
    const ids = rows.map((r) => r.fileId);
    api.getAdminMaterialsByIds(ids).then(({ items }) => {
      const map: Record<string, MaterialDetailItem> = {};
      items.forEach((it) => { if (it) map[it.id] = it; });
      setDetailMap(map);
    }).catch(() => {}).finally(() => setDetailsLoading(false));
  }, [rows]);

  return (
    <Box sx={chartCardSx}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        {title} ({rows.length})
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.5)' }}>
          No {title.toLowerCase()} linked to this project.
        </Typography>
      ) : detailsLoading ? (
        <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress size={20} /></Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.5 }}>
          {rows.map((row) => {
            const detail = detailMap[row.fileId];
            const name = detail?.name ?? `ID: ${row.fileId}`;
            const filename = detail?.filename ?? `${row.fileId}.pdf`;
            const categoryPart = detail?.category ? `${detail.category} · ` : '';
            const subtitle = `${categoryPart}${formatCount(fileRowPreviews(row, materialAppSource))} previews · ${formatCount(fileRowDownloads(row, materialAppSource))} downloads`;
            return (
              <DownloadFileRow
                key={row.fileId}
                filename={filename}
                displayName={name}
                subtitle={subtitle}
                url={detail?.downloadUrl ?? null}
                urlPublic={null}
                previewImage={detail?.thumbnail ?? null}
                uploadedAt={detail?.createdAt ?? null}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const ProjectMaterialsSubTab: React.FC<{
  loading: boolean;
  error: string;
  data: ProjectMaterialAnalyticsResponse | null;
  metric: 'previews' | 'downloads';
  materialAppSource: MaterialAppSource;
  onMetricChange: (m: 'previews' | 'downloads') => void;
  onMaterialAppSourceChange: (source: MaterialAppSource) => void;
  onRefresh: () => void;
  chartGridStroke: string;
  chartAxisStroke: string;
  chartTickStyle: { fill: string; fontSize: number };
}> = ({
  loading,
  error,
  data,
  metric,
  materialAppSource,
  onMetricChange,
  onMaterialAppSourceChange,
  onRefresh,
  chartGridStroke,
  chartAxisStroke,
  chartTickStyle,
}) => {
  const edmDownloaders = React.useMemo(() => {
    if (!data) return [];
    if (materialAppSource === 'super_picker') return data.topEdmDownloadersSp;
    if (materialAppSource === 'project_essential') return data.topEdmDownloadersPe;
    return data.topEdmDownloaders;
  }, [data, materialAppSource]);

  const ebookDownloaders = React.useMemo(() => {
    if (!data) return [];
    if (materialAppSource === 'super_picker') return data.topEbookDownloadersSp;
    if (materialAppSource === 'project_essential') return data.topEbookDownloadersPe;
    return data.topEbookDownloaders;
  }, [data, materialAppSource]);

  const renderTable = (
    title: string,
    rows: ProjectMaterialTopDownloaderRow[],
  ) => (
    <Box sx={chartCardSx}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        {title}
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.5)' }}>
          No downloads in this period.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>CEA</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell align="right">Downloads</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={row.ceaNumber}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{row.ceaNumber}</TableCell>
                  <TableCell>{row.name || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{row.mobile || '—'}</TableCell>
                  <TableCell align="right">{row.downloads}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  return (
    <Stack spacing={2.5}>
      <MaterialActivityLineChart
        title="EDM & eBook over time"
        loading={loading}
        error={error}
        series={data?.dailySeries ?? []}
        metric={metric}
        onMetricChange={onMetricChange}
        materialAppSource={materialAppSource}
        onMaterialAppSourceChange={onMaterialAppSourceChange}
        onRefresh={onRefresh}
        showSourceToggle
        chartGridStroke={chartGridStroke}
        chartAxisStroke={chartAxisStroke}
        chartTickStyle={chartTickStyle}
      />

      {loading ? null : (
        <>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <Box sx={{ width: '100%' }}>
                {renderTable('Top 10 EDM Downloaders', edmDownloaders)}
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <Box sx={{ width: '100%' }}>
                {renderTable('Top 10 eBook Downloaders', ebookDownloaders)}
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <Box sx={{ width: '100%' }}>
                <ProjectMaterialsFileList title="EDMs" rows={data?.edmFiles ?? []} materialAppSource={materialAppSource} />
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <Box sx={{ width: '100%' }}>
                <ProjectMaterialsFileList title="eBooks" rows={data?.ebookFiles ?? []} materialAppSource={materialAppSource} />
              </Box>
            </Grid>
          </Grid>
        </>
      )}
    </Stack>
  );
};

const TopMaterialsList: React.FC<{ label: string; rows: MaterialEngagementTopMaterial[] }> = ({ label, rows }) => {
  const [detailMap, setDetailMap] = React.useState<Record<string, MaterialDetailItem>>({});
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (rows.length === 0) { setDetailMap({}); return; }
    setLoading(true);
    const ids = rows.map((r) => r.fileId);
    api.getAdminMaterialsByIds(ids).then(({ items }) => {
      const map: Record<string, MaterialDetailItem> = {};
      items.forEach((it) => { if (it) map[it.id] = it; });
      setDetailMap(map);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [rows]);

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
        Top {label}s by Interactions
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.45)' }}>
          No {label} data for this period.
        </Typography>
      ) : loading ? (
        <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress size={20} /></Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.5 }}>
          {rows.map((m) => {
            const detail = detailMap[m.fileId];
            const name = detail?.name ?? `ID: ${m.fileId}`;
            const subtitle = detail?.category ?? undefined;
            const filename = detail?.filename ?? `${m.fileId}.pdf`;
            return (
              <DownloadFileRow
                key={m.fileId}
                filename={filename}
                displayName={name}
                subtitle={subtitle}
                url={detail?.downloadUrl ?? null}
                urlPublic={null}
                previewImage={detail?.thumbnail ?? null}
                uploadedAt={detail?.createdAt ?? null}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};


const AnalyticsDashboardDialog: React.FC<AnalyticsDashboardDialogProps> = ({ open, onClose }) => {
  const muiTheme = useMuiTheme();
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('byDate');
  const [draftUserCea, setDraftUserCea] = useState<string>('');
  const [appliedUserCea, setAppliedUserCea] = useState<string>('');
  const [overviewRange, setOverviewRange] = useState<DateRangeState>(() => createAllTimeRange());
  const [overviewGranularity, setOverviewGranularity] = useState<OverviewGranularity>('day');
  const [userRange, setUserRange] = useState<DateRangeState>(() => createAllTimeRange());
  const [overviewData, setOverviewData] = useState<AnalyticsDashboardResponse | null>(null);
  const [userData, setUserData] = useState<AnalyticsDashboardResponse | null>(null);
  const [overviewLoading, setOverviewLoading] = useState<boolean>(false);
  const [userLoading, setUserLoading] = useState<boolean>(false);
  const [overviewError, setOverviewError] = useState<string>('');
  const [userError, setUserError] = useState<string>('');
  const [materialEngagementData, setMaterialEngagementData] = useState<MaterialEngagementResponse | null>(null);
  const [materialEngagementLoading, setMaterialEngagementLoading] = useState<boolean>(false);
  const [materialEngagementError, setMaterialEngagementError] = useState<string>('');
  const [materialType, setMaterialType] = useState<'edm' | 'ebook'>('edm');
  const [materialAppSource, setMaterialAppSource] = useState<MaterialAppSource>('all');
  const [userProfile, setUserProfile] = useState<AgentProfile | null>(null);
  const [userAllSoldUnits, setUserAllSoldUnits] = useState<AgentSoldUnit[]>([]);
  const [userProfileLoading, setUserProfileLoading] = useState<boolean>(false);
  const [userProjectSoldCounts, setUserProjectSoldCounts] = useState<Record<string, number>>({});
  const [userSoldUnitProject, setUserSoldUnitProject] = useState<string | null>(null);
  const [userSoldUnitDetails, setUserSoldUnitDetails] = useState<AgentSoldUnit[]>([]);
  const [userSoldUnitDetailsLoading, setUserSoldUnitDetailsLoading] = useState<boolean>(false);
  const [projectRange, setProjectRange] = useState<DateRangeState>(() => createAllTimeRange());
  const [draftProject, setDraftProject] = useState<string>('');
  const [appliedProject, setAppliedProject] = useState<string>('');
  const [projectData, setProjectData] = useState<ProjectAnalyticsResponse | null>(null);
  const [projectLoading, setProjectLoading] = useState<boolean>(false);
  const [projectError, setProjectError] = useState<string>('');
  const [projectMainSubTab, setProjectMainSubTab] = useState<'activity' | 'materials'>('activity');
  const [projectMaterialData, setProjectMaterialData] = useState<ProjectMaterialAnalyticsResponse | null>(null);
  const [projectMaterialLoading, setProjectMaterialLoading] = useState<boolean>(false);
  const [projectMaterialError, setProjectMaterialError] = useState<string>('');
  const [projectMaterialMetric, setProjectMaterialMetric] = useState<'previews' | 'downloads'>('previews');
  const [projectMaterialAppSource, setProjectMaterialAppSource] = useState<MaterialAppSource>('all');
  const [projectList, setProjectList] = useState<AnalyticsProjectStat[]>([]);
  const [projectListLoading, setProjectListLoading] = useState<boolean>(false);
  const [projectTabSoldRateLimited, setProjectTabSoldRateLimited] = useState<boolean>(false);
  const [projectTabSortField, setProjectTabSortField] = useState<ProjectTabSortField>('viewsPeriod');
  const [projectTabSortDir, setProjectTabSortDir] = useState<SortDirection>('desc');
  const [projectTabFilter, setProjectTabFilter] = useState<string>('');
  const [projectTabUsersPage, setProjectTabUsersPage] = useState<number>(0);
  const PROJECT_TAB_USERS_PAGE_SIZE = 50;
  const [projectTabHideZeroViewsAllTime, setProjectTabHideZeroViewsAllTime] = useState<boolean>(false);
  const [projectTabHideZeroViewsPeriod, setProjectTabHideZeroViewsPeriod] = useState<boolean>(false);
  const [projectTabHideZeroSoldAllTime, setProjectTabHideZeroSoldAllTime] = useState<boolean>(false);
  const [projectTabHideZeroSoldPeriod, setProjectTabHideZeroSoldPeriod] = useState<boolean>(false);
  const [projectTabRemoveConvertedAgents, setProjectTabRemoveConvertedAgents] = useState<boolean>(false);
  const [projectTabHideNonZeroCloseNl, setProjectTabHideNonZeroCloseNl] = useState<boolean>(false);
  const [projectTabOnlyResaleAgent, setProjectTabOnlyResaleAgent] = useState<boolean>(false);
  const [projectSubTab, setProjectSubTab] = useState<ProjectTabSubTab>('top');
  const [projectTabEois, setProjectTabEois] = useState<ProjectEoiAgentRow[]>([]);
  const [projectTabEoisLoading, setProjectTabEoisLoading] = useState<boolean>(false);
  const [projectTabEoiSortField, setProjectTabEoiSortField] = useState<DrillDownEoiSortField>('eoiCount');
  const [projectTabEoiSortDir, setProjectTabEoiSortDir] = useState<SortDirection>('desc');
  const [projectTabEoiSending, setProjectTabEoiSending] = useState<Record<string, boolean>>({});
  const [projectTabEoiSendError, setProjectTabEoiSendError] = useState<string>('');
  const [drillDownProject, setDrillDownProject] = useState<AnalyticsProjectStat | null>(null);
  const [drillDownTopUsers, setDrillDownTopUsers] = useState<ProjectAnalyticsTopUserRow[]>([]);
  const [drillDownLoading, setDrillDownLoading] = useState<boolean>(false);
  const [drillDownSoldRateLimited, setDrillDownSoldRateLimited] = useState<boolean>(false);
  const [drillDownSortField, setDrillDownSortField] = useState<ProjectTabSortField>('viewsPeriod');
  const [drillDownSortDir, setDrillDownSortDir] = useState<SortDirection>('desc');
  const [drillDownFilter, setDrillDownFilter] = useState<string>('');
  const [drillDownHideZeroViewsAllTime, setDrillDownHideZeroViewsAllTime] = useState<boolean>(false);
  const [drillDownHideZeroViewsPeriod, setDrillDownHideZeroViewsPeriod] = useState<boolean>(false);
  const [drillDownHideZeroSoldAllTime, setDrillDownHideZeroSoldAllTime] = useState<boolean>(false);
  const [drillDownHideZeroSoldPeriod, setDrillDownHideZeroSoldPeriod] = useState<boolean>(false);
  const [drillDownRemoveConvertedAgents, setDrillDownRemoveConvertedAgents] = useState<boolean>(false);
  const [drillDownHideNonZeroCloseNl, setDrillDownHideNonZeroCloseNl] = useState<boolean>(false);
  const [drillDownOnlyResaleAgent, setDrillDownOnlyResaleAgent] = useState<boolean>(false);
  const [drillDownTab, setDrillDownTab] = useState<DrillDownTab>('all');
  const [drillDownEois, setDrillDownEois] = useState<ProjectEoiAgentRow[]>([]);
  const [drillDownEoisLoading, setDrillDownEoisLoading] = useState<boolean>(false);
  const [drillDownEoiSortField, setDrillDownEoiSortField] = useState<DrillDownEoiSortField>('eoiCount');
  const [drillDownEoiSortDir, setDrillDownEoiSortDir] = useState<SortDirection>('desc');
  const [drillDownEoiSending, setDrillDownEoiSending] = useState<Record<string, boolean>>({});
  const [drillDownEoiSendError, setDrillDownEoiSendError] = useState<string>('');
  const [soldUnitsDialogContext, setSoldUnitsDialogContext] = useState<{
    ceaNumber: string;
    agentName: string;
  } | null>(null);
  const [soldUnits, setSoldUnits] = useState<AgentSoldUnit[]>([]);
  const [soldUnitsLoading, setSoldUnitsLoading] = useState<boolean>(false);

  // Success Closer tab state
  const [successCloserRange, setSuccessCloserRange] = useState<DateRangeState>(() => createAllTimeRange());
  const [successCloserRows, setSuccessCloserRows] = useState<SuccessCloserRow[]>([]);
  const [successCloserLoading, setSuccessCloserLoading] = useState<boolean>(false);
  const [successCloserError, setSuccessCloserError] = useState<string>('');
  const [successCloserShowTagger, setSuccessCloserShowTagger] = useState<boolean>(true);
  const [successCloserShowMainEntity, setSuccessCloserShowMainEntity] = useState<boolean>(true);
  const [successCloserTxDialog, setSuccessCloserTxDialog] = useState<SuccessCloserRow | null>(null);
  const [byDateRange, setByDateRange] = useState<DateRangeState>(() => createAllTimeRange());
  const [byDateRows, setByDateRows] = useState<AdminByDateAnalyticsRow[]>([]);
  const [byDateUniqueSummary, setByDateUniqueSummary] = useState<AdminByDateAnalyticsSummary>({
    uniqueTransactionCount: 0,
    uniqueTransactedAmount: 0,
    convertedTransactionCount: 0,
    convertedTransactedAmount: 0,
    convertedActivityCount: 0,
    convertedAgentCount: 0,
  });
  const [byDateLoading, setByDateLoading] = useState<boolean>(false);
  const [byDateError, setByDateError] = useState<string>('');
  const [byDateHasSearched, setByDateHasSearched] = useState<boolean>(false);
  const [byDateResultTab, setByDateResultTab] = useState<ByDateResultTab>('activityAndTransaction');
  const [byDateSearch, setByDateSearch] = useState<string>('');
  const [byDateSortField, setByDateSortField] = useState<ByDateSortField>('qualifyingTransactionCount');
  const [byDateSortDir, setByDateSortDir] = useState<SortDirection>('desc');
  const [byDateActivityDialog, setByDateActivityDialog] = useState<ByDateDrillContext | null>(null);
  const [byDateActivityLoading, setByDateActivityLoading] = useState<boolean>(false);
  const [byDateActivityError, setByDateActivityError] = useState<string>('');
  const [byDateActivityUsage, setByDateActivityUsage] = useState<AnalyticsDashboardResponse['userUsage']>(null);
  const [byDateTxDialog, setByDateTxDialog] = useState<ByDateDrillContext | null>(null);
  const [byDateTxLoading, setByDateTxLoading] = useState<boolean>(false);
  const [byDateTxError, setByDateTxError] = useState<string>('');
  const [byDateTxUnits, setByDateTxUnits] = useState<AgentSoldUnit[]>([]);
  const [byDateQualifyingTxDialog, setByDateQualifyingTxDialog] = useState<ByDateDrillContext | null>(null);
  const [byDateQualifyingTxLoading, setByDateQualifyingTxLoading] = useState<boolean>(false);
  const [byDateQualifyingTxError, setByDateQualifyingTxError] = useState<string>('');
  const [byDateQualifyingTxList, setByDateQualifyingTxList] = useState<AdminByDateQualifyingTransactionDetail[]>([]);
  const [byDateQualifyingActivityDialog, setByDateQualifyingActivityDialog] = useState<ByDateDrillContext | null>(null);
  const [byDateQualifyingActivityLoading, setByDateQualifyingActivityLoading] = useState<boolean>(false);
  const [byDateQualifyingActivityError, setByDateQualifyingActivityError] = useState<string>('');
  const [byDateQualifyingActivityList, setByDateQualifyingActivityList] = useState<AdminByDateQualifyingActivityProjectDetail[]>([]);
  const [byDateAllTxDialog, setByDateAllTxDialog] = useState<'amount' | 'count' | null>(null);
  const [byDateAllTxScope, setByDateAllTxScope] = useState<'all' | 'converted'>('all');
  const [byDateAllTxLoading, setByDateAllTxLoading] = useState<boolean>(false);
  const [byDateAllTxError, setByDateAllTxError] = useState<string>('');
  const [byDateAllTxList, setByDateAllTxList] = useState<AdminByDateQualifyingTransactionWithAgent[]>([]);
  const [byDateAllActivityDialogOpen, setByDateAllActivityDialogOpen] = useState<boolean>(false);
  const [byDateAllActivityScope, setByDateAllActivityScope] = useState<'all' | 'converted'>('all');
  const [byDateAllAgentsDialogOpen, setByDateAllAgentsDialogOpen] = useState<boolean>(false);
  const [byDateAllAgentsScope, setByDateAllAgentsScope] = useState<'all' | 'converted'>('all');
  const [byDateConvertedSectionOpen, setByDateConvertedSectionOpen] = useState(false);
  const [usersOverTimeOpen, setUsersOverTimeOpen] = useState(false);
  const [byDateAllTxSort, setByDateAllTxSort] = useState<{ field: 'transactionNo' | 'projectName' | 'unitNo' | 'optionDate' | 'transactionPrice'; dir: SortDirection }>({ field: 'optionDate', dir: 'desc' });
  const [byDateActivityDialogSort, setByDateActivityDialogSort] = useState<{ field: 'name' | 'ceaNumber' | 'tx' | 'activity'; dir: SortDirection }>({ field: 'activity', dir: 'desc' });
  const [byDateAgentsDialogSort, setByDateAgentsDialogSort] = useState<{ field: 'name' | 'ceaNumber' | 'mobile' | 'email' | 'tx' | 'activity' | 'amount'; dir: SortDirection }>({ field: 'tx', dir: 'desc' });

  // JY Users tab state
  const [jyUsersRange, setJyUsersRange] = useState<DateRangeState>(() => createAllTimeRange());
  const [jyUsersRows, setJyUsersRows] = useState<AdminJyUserRow[]>([]);
  const [jyUsersLoading, setJyUsersLoading] = useState<boolean>(false);
  const [jyUsersError, setJyUsersError] = useState<string>('');
  const [jyUsersHasSearched, setJyUsersHasSearched] = useState<boolean>(false);
  const [jyUsersSearch, setJyUsersSearch] = useState<string>('');
  const [jyUsersUsageFilter, setJyUsersUsageFilter] = useState<'all' | 'used' | 'notUsed'>('all');
  const [jyUsersSortField, setJyUsersSortField] = useState<JyUsersSortField>('activityCount');
  const [jyUsersSortDir, setJyUsersSortDir] = useState<SortDirection>('desc');

  const chartGridStroke = muiTheme.palette.mode === 'dark'
    ? 'rgba(255,255,255,0.14)'
    : 'rgba(31,41,68,0.12)';
  const chartAxisStroke = muiTheme.palette.mode === 'dark'
    ? 'rgba(255,255,255,0.3)'
    : 'rgba(31,41,68,0.24)';
  const chartTickStyle = useMemo(
    () => ({ fill: muiTheme.palette.text.secondary, fontSize: 12 }),
    [muiTheme.palette.text.secondary]
  );
  const chartTickStyleSmall = useMemo(
    () => ({ fill: muiTheme.palette.text.secondary, fontSize: 10 }),
    [muiTheme.palette.text.secondary]
  );

  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError('');
    try {
      const response = await api.getAdminAnalyticsDashboard({
        startDate: overviewRange.startDate,
        endDate: overviewRange.endDate,
        granularity: overviewGranularity,
      });
      setOverviewData(response);
    } catch (err: any) {
      setOverviewError(err?.message || 'Failed to load overview analytics');
      setOverviewData(null);
    } finally {
      setOverviewLoading(false);
    }
  }, [overviewRange.endDate, overviewRange.startDate, overviewGranularity]);

  const fetchUserProfile = useCallback(async (cea: string) => {
    setUserProfileLoading(true);
    try {
      const [profileRes, soldRes] = await Promise.all([
        api.getAdminAgentProfile(cea),
        api.getAdminAgentAllSoldUnits(cea),
      ]);
      const matchingSoldUnits = filterSoldUnitsForCea(soldRes.units, cea);
      setUserProfile(profileRes.profile);
      setUserAllSoldUnits(matchingSoldUnits);
      // Build sold count per project for the project views table (normalize to uppercase for matching)
      const uniqueSoldUnits = dedupeSoldUnitTransactions(matchingSoldUnits);
      const countMap: Record<string, number> = {};
      for (const u of uniqueSoldUnits) {
        const key = (u.reference || '').toUpperCase();
        countMap[key] = (countMap[key] || 0) + 1;
      }
      setUserProjectSoldCounts(countMap);
    } catch {
      setUserProfile(null);
      setUserAllSoldUnits([]);
      setUserProjectSoldCounts({});
    } finally {
      setUserProfileLoading(false);
    }
  }, []);

  const fetchUserAnalytics = useCallback(async (ceaOverride?: string) => {
    const nextCea = (typeof ceaOverride === 'string' ? ceaOverride : appliedUserCea).trim();
    if (!nextCea) {
      setAppliedUserCea('');
      setUserData(null);
      setUserProfile(null);
      setUserAllSoldUnits([]);
      setUserProjectSoldCounts({});
      setUserError('');
      return;
    }

    setUserLoading(true);
    setUserError('');
    try {
      const response = await api.getAdminAnalyticsDashboard({
        startDate: userRange.startDate,
        endDate: userRange.endDate,
        userCea: nextCea,
      });
      setAppliedUserCea(nextCea);
      setUserData(response);
    } catch (err: any) {
      setUserError(err?.message || 'Failed to load user analytics');
      setUserData(null);
    } finally {
      setUserLoading(false);
    }
    // Fetch profile & sold units in parallel (independent of date range)
    void fetchUserProfile(nextCea);
  }, [appliedUserCea, userRange.endDate, userRange.startDate, fetchUserProfile]);

  const fetchMaterialEngagement = useCallback(async () => {
    const { startDate, endDate } = userRange;
    if (!startDate || !endDate) return;
    setMaterialEngagementLoading(true);
    setMaterialEngagementError('');
    try {
      const response = await api.getAdminMaterialEngagement({
        startDate,
        endDate,
        ceaNumber: appliedUserCea || undefined,
      });
      setMaterialEngagementData(response);
    } catch (err: any) {
      setMaterialEngagementError(err?.message || 'Failed to load material engagement analytics');
      setMaterialEngagementData(null);
    } finally {
      setMaterialEngagementLoading(false);
    }
  }, [userRange, appliedUserCea]);

  const fetchDrillDown = useCallback(
    async (project: AnalyticsProjectStat) => {
      setDrillDownLoading(true);
      setDrillDownSoldRateLimited(false);
      try {
        const response = await api.getAdminProjectAnalytics({
          projectName: project.projectName,
          startDate: overviewRange.startDate,
          endDate: overviewRange.endDate,
        });
        setDrillDownTopUsers(response.topUsers);
        setDrillDownSoldRateLimited(Boolean(response.soldCountsRateLimited));
      } catch {
        setDrillDownTopUsers([]);
        setDrillDownSoldRateLimited(false);
      } finally {
        setDrillDownLoading(false);
      }
    },
    [overviewRange.endDate, overviewRange.startDate]
  );

  useEffect(() => {
    if (!drillDownProject) {
      return;
    }
    void fetchDrillDown(drillDownProject);
  }, [drillDownProject, overviewRange.startDate, overviewRange.endDate, fetchDrillDown]);

  const fetchDrillDownEois = useCallback(async (project: AnalyticsProjectStat) => {
    setDrillDownEoisLoading(true);
    try {
      const response = await api.getAdminProjectEois(project.projectName);
      setDrillDownEois(response.agents);
    } catch {
      setDrillDownEois([]);
    } finally {
      setDrillDownEoisLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!drillDownProject || drillDownTab !== 'eoi') {
      return;
    }
    void fetchDrillDownEois(drillDownProject);
  }, [drillDownProject, drillDownTab, fetchDrillDownEois]);

  const handleBarClick = useCallback(
    (data: AnalyticsProjectStat) => {
      setDrillDownProject(data);
      setDrillDownFilter('');
      setDrillDownHideZeroViewsAllTime(false);
      setDrillDownHideZeroViewsPeriod(false);
      setDrillDownHideZeroSoldAllTime(false);
      setDrillDownHideZeroSoldPeriod(false);
      setDrillDownRemoveConvertedAgents(false);
      setDrillDownHideNonZeroCloseNl(false);
      setDrillDownSortField('viewsPeriod');
      setDrillDownSortDir('desc');
      setDrillDownTab('all');
      setDrillDownEois([]);
      setDrillDownEoiSortField('eoiCount');
      setDrillDownEoiSortDir('desc');
      setDrillDownEoiSending({});
      setDrillDownEoiSendError('');
    },
    []
  );

  const handleCloseDrillDown = useCallback(() => {
    setDrillDownProject(null);
    setDrillDownRemoveConvertedAgents(false);
    setDrillDownHideNonZeroCloseNl(false);
  }, []);

  const handleSendEoiWhatsApp = useCallback(
    async (ceaNumber: string) => {
      if (!drillDownProject) return;
      setDrillDownEoiSendError('');
      setDrillDownEoiSending((prev) => ({ ...prev, [ceaNumber]: true }));
      try {
        const response = await api.sendAdminProjectEoiWhatsApp(
          drillDownProject.projectName,
          ceaNumber,
        );
        setDrillDownEois((prev) =>
          prev.map((r) => (r.ceaNumber === ceaNumber ? { ...r, engagedCount: response.engagedCount } : r))
        );
      } catch (err) {
        setDrillDownEoiSendError(err instanceof Error ? err.message : 'Failed to send WhatsApp');
      } finally {
        setDrillDownEoiSending((prev) => {
          const next = { ...prev };
          delete next[ceaNumber];
          return next;
        });
      }
    },
    [drillDownProject]
  );

  const handleSoldCountClick = useCallback(
    async (ceaNumber: string, rangeMode: SoldUnitsRangeMode, agentName?: string | null) => {
      if (drillDownSoldRateLimited) return;
      if (!drillDownProject) return;
      setSoldUnitsDialogContext({
        ceaNumber,
        agentName: (agentName || '').trim(),
      });
      setSoldUnitsLoading(true);
      setSoldUnits([]);
      try {
        const response = await api.getAdminAgentSoldUnits(
          drillDownProject.projectName,
          ceaNumber,
          rangeMode === 'selectedPeriod'
            ? { startDate: overviewRange.startDate, endDate: overviewRange.endDate }
            : undefined,
        );
        setSoldUnits(dedupeSoldUnitTransactions(filterSoldUnitsForCea(response.units, ceaNumber)));
      } catch {
        setSoldUnits([]);
      } finally {
        setSoldUnitsLoading(false);
      }
    },
    [drillDownProject, drillDownSoldRateLimited, overviewRange.endDate, overviewRange.startDate]
  );

  const handleUserProjectSoldClick = useCallback(
    (projectName: string) => {
      if (!appliedUserCea) return;
      setUserSoldUnitProject(projectName);
      setUserSoldUnitDetailsLoading(true);
      setUserSoldUnitDetails([]);
      api.getAdminAgentSoldUnits(projectName, appliedUserCea)
        .then((res) => setUserSoldUnitDetails(dedupeSoldUnitTransactions(filterSoldUnitsForCea(res.units, appliedUserCea))))
        .catch(() => setUserSoldUnitDetails([]))
        .finally(() => setUserSoldUnitDetailsLoading(false));
    },
    [appliedUserCea]
  );

  const handleProjectTabSoldClick = useCallback(
    (ceaNumber: string, rangeMode: SoldUnitsRangeMode, agentName?: string | null) => {
      if (projectTabSoldRateLimited) return;
      if (!appliedProject) return;
      setSoldUnitsDialogContext({
        ceaNumber,
        agentName: (agentName || '').trim(),
      });
      setSoldUnitsLoading(true);
      setSoldUnits([]);
      api.getAdminAgentSoldUnits(
        appliedProject,
        ceaNumber,
        rangeMode === 'selectedPeriod'
          ? { startDate: projectRange.startDate, endDate: projectRange.endDate }
          : undefined,
      )
        .then((res) => setSoldUnits(dedupeSoldUnitTransactions(filterSoldUnitsForCea(res.units, ceaNumber))))
        .catch(() => setSoldUnits([]))
        .finally(() => setSoldUnitsLoading(false));
    },
    [appliedProject, projectRange.endDate, projectRange.startDate, projectTabSoldRateLimited]
  );

  const handleProjectTabSort = useCallback((field: ProjectTabSortField) => {
    setProjectTabSortDir((prev) => (projectTabSortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'));
    setProjectTabSortField(field);
  }, [projectTabSortField]);

  const convertedAgentCeaSet = useMemo(() => {
    const set = new Set<string>();
    for (const row of byDateRows) {
      if ((row.convertedTransactionCount || 0) > 0) {
        set.add(row.ceaNumber.trim().toUpperCase());
      }
    }
    return set;
  }, [byDateRows]);

  // Reset pagination whenever the filtered set can change.
  useEffect(() => {
    setProjectTabUsersPage(0);
  }, [
    projectData,
    projectTabFilter,
    projectTabHideZeroViewsAllTime,
    projectTabHideZeroViewsPeriod,
    projectTabHideZeroSoldAllTime,
    projectTabHideZeroSoldPeriod,
    projectTabRemoveConvertedAgents,
    projectTabHideNonZeroCloseNl,
    projectTabOnlyResaleAgent,
    projectTabSortField,
    projectTabSortDir,
  ]);

  const filteredSortedProjectTabUsers = useMemo(() => {
    const topUsers = projectData?.topUsers ?? [];
    let rows = topUsers;
    if (projectTabFilter) {
      const lower = projectTabFilter.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.ceaNumber.toLowerCase().includes(lower) ||
          (r.name || '').toLowerCase().includes(lower) ||
          (r.mobile || '').toLowerCase().includes(lower)
      );
    }
    if (projectTabRemoveConvertedAgents) {
      rows = rows.filter((r) => !convertedAgentCeaSet.has(r.ceaNumber.trim().toUpperCase()));
    }
    if (projectTabHideNonZeroCloseNl) {
      rows = rows.filter((r) => (r.closeNewLaunchCount || 0) === 0);
    }
    if (projectTabOnlyResaleAgent) {
      rows = rows.filter((r) => Boolean(r.isResaleAgent));
    }
    if (projectTabHideZeroViewsAllTime) {
      rows = rows.filter((r) => r.viewsAllTime !== 0);
    }
    if (projectTabHideZeroViewsPeriod) {
      rows = rows.filter((r) => r.viewsPeriod !== 0);
    }
    if (projectTabHideZeroSoldAllTime) {
      rows = rows.filter((r) => r.soldAllTime !== 0);
    }
    if (projectTabHideZeroSoldPeriod) {
      rows = rows.filter((r) => r.soldPeriod !== 0);
    }
    const sorted = [...rows].sort((a, b) => {
      let cmp = 0;
      switch (projectTabSortField) {
        case 'ceaNumber':
          cmp = a.ceaNumber.localeCompare(b.ceaNumber);
          break;
        case 'name':
          cmp = (a.name || '').localeCompare(b.name || '');
          break;
        case 'mobile':
          cmp = (a.mobile || '').localeCompare(b.mobile || '');
          break;
        case 'viewsAllTime':
          cmp = a.viewsAllTime - b.viewsAllTime;
          break;
        case 'viewsPeriod':
          cmp = a.viewsPeriod - b.viewsPeriod;
          break;
        case 'soldAllTime':
          cmp = a.soldAllTime - b.soldAllTime;
          break;
        case 'soldPeriod':
          cmp = a.soldPeriod - b.soldPeriod;
          break;
        case 'closeNewLaunchCount':
          cmp = (a.closeNewLaunchCount || 0) - (b.closeNewLaunchCount || 0);
          break;
      }
      return projectTabSortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [
    projectData,
    projectTabFilter,
    projectTabRemoveConvertedAgents,
    convertedAgentCeaSet,
    projectTabHideNonZeroCloseNl,
    projectTabOnlyResaleAgent,
    projectTabHideZeroViewsAllTime,
    projectTabHideZeroViewsPeriod,
    projectTabHideZeroSoldAllTime,
    projectTabHideZeroSoldPeriod,
    projectTabSortField,
    projectTabSortDir,
  ]);

  const projectTabViewsPeriodByCea = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of projectData?.topUsers ?? []) {
      if (row.ceaNumber) map.set(row.ceaNumber, row.viewsPeriod || 0);
    }
    return map;
  }, [projectData]);

  const fetchProjectTabEois = useCallback(async (projectName: string) => {
    setProjectTabEoisLoading(true);
    try {
      const response = await api.getAdminProjectEois(projectName);
      setProjectTabEois(response.agents);
    } catch {
      setProjectTabEois([]);
    } finally {
      setProjectTabEoisLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!appliedProject || projectSubTab !== 'eoi') {
      return;
    }
    void fetchProjectTabEois(appliedProject);
  }, [appliedProject, projectSubTab, fetchProjectTabEois]);

  const handleProjectTabEoiSort = useCallback((field: DrillDownEoiSortField) => {
    setProjectTabEoiSortDir((prev) => (projectTabEoiSortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'));
    setProjectTabEoiSortField(field);
  }, [projectTabEoiSortField]);

  const filteredSortedProjectTabEois = useMemo(() => {
    let rows = projectTabEois.map((r) => ({
      ...r,
      viewsPeriod: projectTabViewsPeriodByCea.get(r.ceaNumber) ?? 0,
    }));
    if (projectTabFilter) {
      const lower = projectTabFilter.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.ceaNumber.toLowerCase().includes(lower) ||
          (r.name || '').toLowerCase().includes(lower) ||
          (r.mobile || '').toLowerCase().includes(lower)
      );
    }
    if (projectTabRemoveConvertedAgents) {
      rows = rows.filter((r) => !convertedAgentCeaSet.has(r.ceaNumber.trim().toUpperCase()));
    }
    if (projectTabHideNonZeroCloseNl) {
      rows = rows.filter((r) => (r.closeNewLaunchCount || 0) === 0);
    }
    const sorted = [...rows].sort((a, b) => {
      let cmp = 0;
      switch (projectTabEoiSortField) {
        case 'ceaNumber':
          cmp = a.ceaNumber.localeCompare(b.ceaNumber);
          break;
        case 'name':
          cmp = (a.name || '').localeCompare(b.name || '');
          break;
        case 'mobile':
          cmp = (a.mobile || '').localeCompare(b.mobile || '');
          break;
        case 'viewsPeriod':
          cmp = a.viewsPeriod - b.viewsPeriod;
          break;
        case 'eoiCount':
          cmp = a.eoiCount - b.eoiCount;
          break;
        case 'closeNewLaunchCount':
          cmp = (a.closeNewLaunchCount || 0) - (b.closeNewLaunchCount || 0);
          break;
      }
      return projectTabEoiSortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [
    projectTabEois,
    projectTabViewsPeriodByCea,
    projectTabFilter,
    projectTabRemoveConvertedAgents,
    convertedAgentCeaSet,
    projectTabHideNonZeroCloseNl,
    projectTabEoiSortField,
    projectTabEoiSortDir,
  ]);

  const handleSendProjectTabEoiWhatsApp = useCallback(
    async (ceaNumber: string) => {
      if (!appliedProject) return;
      setProjectTabEoiSendError('');
      setProjectTabEoiSending((prev) => ({ ...prev, [ceaNumber]: true }));
      try {
        const response = await api.sendAdminProjectEoiWhatsApp(appliedProject, ceaNumber);
        setProjectTabEois((prev) =>
          prev.map((r) => (r.ceaNumber === ceaNumber ? { ...r, engagedCount: response.engagedCount } : r))
        );
      } catch (err) {
        setProjectTabEoiSendError(err instanceof Error ? err.message : 'Failed to send WhatsApp');
      } finally {
        setProjectTabEoiSending((prev) => {
          const next = { ...prev };
          delete next[ceaNumber];
          return next;
        });
      }
    },
    [appliedProject]
  );

  const fetchProjectList = useCallback(async () => {
    setProjectListLoading(true);
    try {
      const sitePlans = await api.getTopViewingSitePlans();
      const normalized = (sitePlans ?? [])
        .map((sp) => ({
          projectName: (sp.projectName || '').trim(),
          // The analytics charts use GA4, but this dropdown should only reflect
          // which projects exist on the site-plan page.
          views: 0,
        }))
        .filter((p) => p.projectName.length > 0);

      normalized.sort((a, b) => a.projectName.localeCompare(b.projectName, undefined, { sensitivity: 'base' }));
      setProjectList(normalized);
    } catch {
      setProjectList([]);
    } finally {
      setProjectListLoading(false);
    }
  }, []);

  const fetchProjectAnalytics = useCallback(async (projectOverride?: string) => {
    const projectName = (typeof projectOverride === 'string' ? projectOverride : appliedProject).trim();
    if (!projectName) {
      setAppliedProject('');
      setProjectData(null);
      setProjectError('');
      setProjectTabSoldRateLimited(false);
      return;
    }
    setProjectLoading(true);
    setProjectError('');
    setProjectTabSoldRateLimited(false);
    try {
      const response = await api.getAdminProjectAnalytics({
        projectName,
        startDate: projectRange.startDate,
        endDate: projectRange.endDate,
      });
      setAppliedProject(projectName);
      setProjectData(response);
      setProjectTabFilter('');
      setProjectTabHideZeroViewsAllTime(false);
      setProjectTabHideZeroViewsPeriod(false);
      setProjectTabHideZeroSoldAllTime(false);
      setProjectTabHideZeroSoldPeriod(false);
      setProjectTabSortField('viewsPeriod');
      setProjectTabSortDir('desc');
      setProjectTabSoldRateLimited(Boolean(response.soldCountsRateLimited));
      setProjectSubTab('top');
      setProjectTabEois([]);
      setProjectTabEoiSortField('eoiCount');
      setProjectTabEoiSortDir('desc');
      setProjectTabEoiSending({});
      setProjectTabEoiSendError('');
      setProjectMainSubTab('activity');
      setProjectMaterialData(null);
      setProjectMaterialError('');
      setProjectMaterialMetric('previews');
    } catch (err: any) {
      setProjectError(err?.message || 'Failed to load project analytics');
      setProjectData(null);
      setProjectTabSoldRateLimited(false);
    } finally {
      setProjectLoading(false);
    }
  }, [appliedProject, projectRange.endDate, projectRange.startDate]);

  const fetchProjectMaterialAnalytics = useCallback(async () => {
    if (!appliedProject) return;
    setProjectMaterialLoading(true);
    setProjectMaterialError('');
    try {
      const response = await api.getAdminProjectMaterialAnalytics({
        projectName: appliedProject,
        startDate: projectRange.startDate,
        endDate: projectRange.endDate,
      });
      setProjectMaterialData(response);
    } catch (err: any) {
      setProjectMaterialError(err?.message || 'Failed to load project material analytics');
      setProjectMaterialData(null);
    } finally {
      setProjectMaterialLoading(false);
    }
  }, [appliedProject, projectRange.endDate, projectRange.startDate]);

  useEffect(() => {
    if (projectMainSubTab === 'materials' && appliedProject) {
      void fetchProjectMaterialAnalytics();
    }
  }, [projectMainSubTab, appliedProject, projectRange.startDate, projectRange.endDate, fetchProjectMaterialAnalytics]);

  const fetchSuccessCloser = useCallback(async () => {
    setSuccessCloserLoading(true);
    setSuccessCloserError('');
    try {
      const response = await api.getAdminSuccessCloser({
        startDate: successCloserRange.startDate,
        endDate: successCloserRange.endDate,
      });
      setSuccessCloserRows(response.rows);
    } catch (err: any) {
      setSuccessCloserError(err?.message || 'Failed to load success closer data');
      setSuccessCloserRows([]);
    } finally {
      setSuccessCloserLoading(false);
    }
  }, [successCloserRange.startDate, successCloserRange.endDate]);

  const fetchByDate = useCallback(async () => {
    setByDateLoading(true);
    setByDateError('');
    setByDateHasSearched(true);
    try {
      const response = await api.getAdminByDateAnalytics({
        startDate: byDateRange.startDate,
        endDate: byDateRange.endDate,
      });
      setByDateRows(response.rows);
      setByDateUniqueSummary(response.summary || EMPTY_BY_DATE_SUMMARY);
    } catch (err: any) {
      setByDateError(err?.message || 'Failed to load by-date analytics');
      setByDateRows([]);
      setByDateUniqueSummary(EMPTY_BY_DATE_SUMMARY);
    } finally {
      setByDateLoading(false);
    }
  }, [byDateRange.startDate, byDateRange.endDate]);

  const fetchJyUsers = useCallback(async () => {
    setJyUsersLoading(true);
    setJyUsersError('');
    setJyUsersHasSearched(true);
    try {
      const response = await api.getAdminJyUsers({
        startDate: jyUsersRange.startDate,
        endDate: jyUsersRange.endDate,
      });
      setJyUsersRows(response.rows);
    } catch (err: any) {
      setJyUsersError(err?.message || 'Failed to load JY users');
      setJyUsersRows([]);
    } finally {
      setJyUsersLoading(false);
    }
  }, [jyUsersRange.startDate, jyUsersRange.endDate]);

  const handleJyUsersSort = useCallback((field: JyUsersSortField) => {
    setJyUsersSortDir((prev) => (jyUsersSortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'));
    setJyUsersSortField(field);
  }, [jyUsersSortField]);

  const jyUsersSummary = useMemo(() => {
    const usedPicker = jyUsersRows.filter((r) => r.usedPicker).length;
    return { total: jyUsersRows.length, usedPicker, notUsedPicker: jyUsersRows.length - usedPicker };
  }, [jyUsersRows]);

  const jyUsersPieData = useMemo(() => ([
    { name: 'Used picker', value: jyUsersSummary.usedPicker, color: '#38B2AC' },
    { name: 'No usage', value: jyUsersSummary.notUsedPicker, color: 'rgba(160,174,192,0.55)' },
  ]), [jyUsersSummary]);

  const filteredSortedJyUsers = useMemo(() => {
    let rows = jyUsersRows;
    if (jyUsersUsageFilter === 'used') {
      rows = rows.filter((r) => r.usedPicker);
    } else if (jyUsersUsageFilter === 'notUsed') {
      rows = rows.filter((r) => !r.usedPicker);
    }
    if (jyUsersSearch.trim()) {
      const lower = jyUsersSearch.trim().toLowerCase();
      rows = rows.filter(
        (r) =>
          r.ceaNumber.toLowerCase().includes(lower) ||
          (r.name || '').toLowerCase().includes(lower) ||
          (r.mobile || '').toLowerCase().includes(lower) ||
          (r.email || '').toLowerCase().includes(lower)
      );
    }
    const sorted = [...rows].sort((a, b) => {
      let cmp = 0;
      switch (jyUsersSortField) {
        case 'name':
          cmp = (a.name || '').localeCompare(b.name || '');
          break;
        case 'ceaNumber':
          cmp = a.ceaNumber.localeCompare(b.ceaNumber, undefined, { numeric: true });
          break;
        case 'mobile':
          cmp = (a.mobile || '').localeCompare(b.mobile || '');
          break;
        case 'resalePercent':
          cmp = a.resalePercent - b.resalePercent;
          break;
        case 'activityCount':
          cmp = a.activityCount - b.activityCount;
          break;
      }
      return jyUsersSortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [jyUsersRows, jyUsersUsageFilter, jyUsersSearch, jyUsersSortField, jyUsersSortDir]);

  const handleByDateSort = useCallback((field: ByDateSortField) => {
    setByDateSortDir((prev) => (byDateSortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'));
    setByDateSortField(field);
  }, [byDateSortField]);

  const handleByDateActivityClick = useCallback(async (row: AdminByDateAnalyticsRow) => {
    if (row.activityCount <= 0) {
      return;
    }
    setByDateActivityDialog({
      ceaNumber: row.ceaNumber,
      name: row.name?.trim() || row.ceaNumber,
    });
    setByDateActivityLoading(true);
    setByDateActivityError('');
    setByDateActivityUsage(null);
    try {
      const response = await api.getAdminAnalyticsDashboard({
        startDate: byDateRange.startDate,
        endDate: byDateRange.endDate,
        userCea: row.ceaNumber,
      });
      setByDateActivityUsage(response.userUsage);
    } catch (err: any) {
      setByDateActivityError(err?.message || 'Failed to load picker activity details');
      setByDateActivityUsage(null);
    } finally {
      setByDateActivityLoading(false);
    }
  }, [byDateRange.endDate, byDateRange.startDate]);

  const handleByDateTransactionClick = useCallback(async (row: AdminByDateAnalyticsRow) => {
    if (row.transactionCount <= 0) {
      return;
    }
    setByDateTxDialog({
      ceaNumber: row.ceaNumber,
      name: row.name?.trim() || row.ceaNumber,
    });
    setByDateTxLoading(true);
    setByDateTxError('');
    setByDateTxUnits([]);
    try {
      const response = await api.getAdminAgentAllSoldUnits(row.ceaNumber);
      const filtered = response.units.filter((unit) => {
        const optionDate = (unit.optionDate || '').trim();
        return Boolean(optionDate) && optionDate >= byDateRange.startDate && optionDate <= byDateRange.endDate;
      });
      setByDateTxUnits(dedupeSoldUnitTransactions(filterSoldUnitsForCea(filtered, row.ceaNumber)));
    } catch (err: any) {
      setByDateTxError(err?.message || 'Failed to load transaction details');
      setByDateTxUnits([]);
    } finally {
      setByDateTxLoading(false);
    }
  }, [byDateRange.endDate, byDateRange.startDate]);

  const handleByDateQualifyingTxClick = useCallback(async (row: AdminByDateAnalyticsRow) => {
    if (row.qualifyingTransactionCount <= 0) {
      return;
    }
    setByDateQualifyingTxDialog({
      ceaNumber: row.ceaNumber,
      name: row.name?.trim() || row.ceaNumber,
    });
    setByDateQualifyingTxLoading(true);
    setByDateQualifyingTxError('');
    setByDateQualifyingTxList([]);
    try {
      const response = await api.getAdminByDateQualifyingDetails({
        cea: row.ceaNumber,
        startDate: byDateRange.startDate,
        endDate: byDateRange.endDate,
      });
      setByDateQualifyingTxList(response.qualifyingTransactions);
    } catch (err: any) {
      setByDateQualifyingTxError(err?.message || 'Failed to load qualifying transactions');
      setByDateQualifyingTxList([]);
    } finally {
      setByDateQualifyingTxLoading(false);
    }
  }, [byDateRange.endDate, byDateRange.startDate]);

  const handleByDateAllQualifyingTxClick = useCallback(async (mode: 'amount' | 'count', scope: 'all' | 'converted' = 'all') => {
    setByDateAllTxDialog(mode);
    setByDateAllTxScope(scope);
    setByDateAllTxLoading(true);
    setByDateAllTxError('');
    setByDateAllTxList([]);
    try {
      const response = await api.getAdminByDateAllQualifyingTransactions({
        startDate: byDateRange.startDate,
        endDate: byDateRange.endDate,
      });
      setByDateAllTxList(response.transactions);
    } catch (err: any) {
      setByDateAllTxError(err?.message || 'Failed to load qualifying transactions');
      setByDateAllTxList([]);
    } finally {
      setByDateAllTxLoading(false);
    }
  }, [byDateRange.endDate, byDateRange.startDate]);

  const handleByDateQualifyingActivityClick = useCallback(async (row: AdminByDateAnalyticsRow) => {
    if (row.qualifyingActivityCount <= 0) {
      return;
    }
    setByDateQualifyingActivityDialog({
      ceaNumber: row.ceaNumber,
      name: row.name?.trim() || row.ceaNumber,
    });
    setByDateQualifyingActivityLoading(true);
    setByDateQualifyingActivityError('');
    setByDateQualifyingActivityList([]);
    try {
      const response = await api.getAdminByDateQualifyingDetails({
        cea: row.ceaNumber,
        startDate: byDateRange.startDate,
        endDate: byDateRange.endDate,
      });
      setByDateQualifyingActivityList(response.qualifyingActivityByProject);
    } catch (err: any) {
      setByDateQualifyingActivityError(err?.message || 'Failed to load qualifying activity');
      setByDateQualifyingActivityList([]);
    } finally {
      setByDateQualifyingActivityLoading(false);
    }
  }, [byDateRange.endDate, byDateRange.startDate]);

  const filteredSuccessCloserRows = useMemo(() => {
    return successCloserRows
      .filter((r) => r.activityCount > 0)
      .map((r) => {
        // Filter transactions: exclude those with no project activities, and apply checkbox filters
        const filtered = r.transactions.filter((tx) => {
          if (tx.projectActivityCount <= 0) return false;
          if (!successCloserShowTagger && (tx.grouping || '').trim().toLowerCase() === 'tagger') return false;
          if (!successCloserShowMainEntity && (tx.agentRole || '').trim().toLowerCase() === 'agentmainentity') return false;
          return true;
        });
        return { ...r, transactions: filtered, closingCount: filtered.length };
      })
      .filter((r) => r.closingCount > 0)
      .sort((a, b) => b.closingCount - a.closingCount);
  }, [successCloserRows, successCloserShowTagger, successCloserShowMainEntity]);

  const byDateBuckets = useMemo<Record<ByDateResultTab, AdminByDateAnalyticsRow[]>>(() => {
    const buckets: Record<ByDateResultTab, AdminByDateAnalyticsRow[]> = {
      activityAndTransaction: [],
      noActivityNoTransaction: [],
      activityNoTransaction: [],
      noActivityTransaction: [],
    };

    for (const row of byDateRows) {
      const hasActivity = row.activityCount > 0;
      const hasTransaction = row.transactionCount > 0;

      if (hasActivity && hasTransaction) {
        buckets.activityAndTransaction.push(row);
      } else if (!hasActivity && !hasTransaction) {
        buckets.noActivityNoTransaction.push(row);
      } else if (hasActivity) {
        buckets.activityNoTransaction.push(row);
      } else {
        buckets.noActivityTransaction.push(row);
      }
    }

    return buckets;
  }, [byDateRows]);

  const byDateQualifyingSummary = useMemo(() => {
    let activityCount = 0;
    let agentCount = 0;
    for (const row of byDateRows) {
      if (row.qualifyingTransactionCount > 0) {
        agentCount += 1;
        activityCount += row.qualifyingActivityCount;
      }
    }
    return {
      transactedAmount: byDateUniqueSummary.uniqueTransactedAmount,
      transactionCount: byDateUniqueSummary.uniqueTransactionCount,
      activityCount,
      agentCount,
    };
  }, [byDateRows, byDateUniqueSummary]);

  const byDateConvertedSummary = useMemo(() => ({
    transactedAmount: byDateUniqueSummary.convertedTransactedAmount,
    transactionCount: byDateUniqueSummary.convertedTransactionCount,
    activityCount: byDateUniqueSummary.convertedActivityCount,
    agentCount: byDateUniqueSummary.convertedAgentCount,
  }), [byDateUniqueSummary]);

  const byDateAllTxListFiltered = useMemo(() => {
    const base = byDateAllTxScope === 'converted' ? byDateAllTxList.filter((t) => t.isConverted) : byDateAllTxList;
    const { field, dir } = byDateAllTxSort;
    const mult = dir === 'asc' ? 1 : -1;
    const cmpStr = (a: string, b: string) => (a || '').localeCompare(b || '') * mult;
    const cmpNum = (a: number, b: number) => ((a || 0) - (b || 0)) * mult;
    return base.slice().sort((a, b) => {
      switch (field) {
        case 'transactionNo': return cmpStr(a.transactionNo, b.transactionNo);
        case 'projectName': return cmpStr(a.projectName, b.projectName);
        case 'unitNo': return cmpStr(a.unitNo, b.unitNo);
        case 'optionDate': return cmpStr(a.optionDate, b.optionDate);
        case 'transactionPrice': return cmpNum(a.transactionPrice, b.transactionPrice);
        default: return 0;
      }
    });
  }, [byDateAllTxList, byDateAllTxScope, byDateAllTxSort]);

  const byDateActivityDialogRows = useMemo(() => {
    const converted = byDateAllActivityScope === 'converted';
    const base = byDateRows.filter((r) => (converted ? r.convertedActivityCount : r.qualifyingActivityCount) > 0);
    const { field, dir } = byDateActivityDialogSort;
    const mult = dir === 'asc' ? 1 : -1;
    return base.slice().sort((a, b) => {
      switch (field) {
        case 'name': return (a.name || '').localeCompare(b.name || '') * mult;
        case 'ceaNumber': return a.ceaNumber.localeCompare(b.ceaNumber, undefined, { numeric: true }) * mult;
        case 'tx': return ((converted ? a.convertedTransactionCount : a.qualifyingTransactionCount)
          - (converted ? b.convertedTransactionCount : b.qualifyingTransactionCount)) * mult;
        case 'activity': return ((converted ? a.convertedActivityCount : a.qualifyingActivityCount)
          - (converted ? b.convertedActivityCount : b.qualifyingActivityCount)) * mult;
        default: return 0;
      }
    });
  }, [byDateRows, byDateAllActivityScope, byDateActivityDialogSort]);

  const byDateAgentsDialogRows = useMemo(() => {
    const converted = byDateAllAgentsScope === 'converted';
    const base = byDateRows.filter((r) => (converted ? r.convertedTransactionCount : r.qualifyingTransactionCount) > 0);
    const { field, dir } = byDateAgentsDialogSort;
    const mult = dir === 'asc' ? 1 : -1;
    return base.slice().sort((a, b) => {
      switch (field) {
        case 'name': return (a.name || '').localeCompare(b.name || '') * mult;
        case 'ceaNumber': return a.ceaNumber.localeCompare(b.ceaNumber, undefined, { numeric: true }) * mult;
        case 'mobile': return (a.mobile || '').localeCompare(b.mobile || '') * mult;
        case 'email': return (a.email || '').localeCompare(b.email || '') * mult;
        case 'tx': return ((converted ? a.convertedTransactionCount : a.qualifyingTransactionCount)
          - (converted ? b.convertedTransactionCount : b.qualifyingTransactionCount)) * mult;
        case 'activity': return ((converted ? a.convertedActivityCount : a.qualifyingActivityCount)
          - (converted ? b.convertedActivityCount : b.qualifyingActivityCount)) * mult;
        case 'amount': return ((converted ? a.convertedTransactionAmount : a.qualifyingTransactionAmount)
          - (converted ? b.convertedTransactionAmount : b.qualifyingTransactionAmount)) * mult;
        default: return 0;
      }
    });
  }, [byDateRows, byDateAllAgentsScope, byDateAgentsDialogSort]);

  const toggleByDateAllTxSort = useCallback((field: typeof byDateAllTxSort.field) => {
    setByDateAllTxSort((prev) => ({ field, dir: prev.field === field ? (prev.dir === 'asc' ? 'desc' : 'asc') : 'desc' }));
  }, []);
  const toggleByDateActivityDialogSort = useCallback((field: typeof byDateActivityDialogSort.field) => {
    setByDateActivityDialogSort((prev) => ({ field, dir: prev.field === field ? (prev.dir === 'asc' ? 'desc' : 'asc') : 'desc' }));
  }, []);
  const toggleByDateAgentsDialogSort = useCallback((field: typeof byDateAgentsDialogSort.field) => {
    setByDateAgentsDialogSort((prev) => ({ field, dir: prev.field === field ? (prev.dir === 'asc' ? 'desc' : 'asc') : 'desc' }));
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    void fetchOverview();
  }, [open, fetchOverview]);

  // Main Overview (byDate): same as other tabs — use current range (default all-time) and refetch when range changes while open.
  useEffect(() => {
    if (!open) {
      return;
    }
    void fetchByDate();
  }, [open, fetchByDate]);

  useEffect(() => {
    if (activeTab === 'project') {
      void fetchProjectList();
    }
  }, [activeTab, fetchProjectList]);

  useEffect(() => {
    if (open && activeTab === 'user') {
      void fetchMaterialEngagement();
    }
  }, [open, activeTab, fetchMaterialEngagement]);

  useEffect(() => {
    if (open && activeTab === 'jyUsers') {
      void fetchJyUsers();
    }
  }, [open, activeTab, fetchJyUsers]);

  useEffect(() => {
    if (!open) {
      setActiveTab('byDate');
      setDraftUserCea('');
      setAppliedUserCea('');
      setOverviewRange(createAllTimeRange());
      setOverviewGranularity('day');
      setUserRange(createAllTimeRange());
      setProjectRange(createAllTimeRange());
      setDraftProject('');
      setAppliedProject('');
      setOverviewData(null);
      setUserData(null);
      setProjectData(null);
      setProjectList([]);
      setOverviewLoading(false);
      setUserLoading(false);
      setProjectLoading(false);
      setOverviewError('');
      setUserError('');
      setProjectError('');
      setDrillDownProject(null);
      setDrillDownTopUsers([]);
      setDrillDownSoldRateLimited(false);
      setProjectTabSoldRateLimited(false);
      setProjectTabHideZeroViewsAllTime(false);
      setProjectTabHideZeroViewsPeriod(false);
      setProjectTabHideZeroSoldAllTime(false);
      setProjectTabHideZeroSoldPeriod(false);
      setProjectTabRemoveConvertedAgents(false);
      setProjectTabHideNonZeroCloseNl(false);
      setProjectSubTab('top');
      setProjectTabEois([]);
      setProjectTabEoisLoading(false);
      setProjectTabEoiSortField('eoiCount');
      setProjectTabEoiSortDir('desc');
      setProjectTabEoiSending({});
      setProjectTabEoiSendError('');
      setDrillDownHideZeroViewsAllTime(false);
      setDrillDownHideZeroViewsPeriod(false);
      setDrillDownHideZeroSoldAllTime(false);
      setDrillDownHideZeroSoldPeriod(false);
      setDrillDownRemoveConvertedAgents(false);
      setDrillDownHideNonZeroCloseNl(false);
      setDrillDownTab('all');
      setDrillDownEois([]);
      setDrillDownEoisLoading(false);
      setDrillDownEoiSortField('eoiCount');
      setDrillDownEoiSortDir('desc');
      setDrillDownEoiSending({});
      setDrillDownEoiSendError('');
      setSuccessCloserRange(createAllTimeRange());
      setSuccessCloserRows([]);
      setSuccessCloserLoading(false);
      setSuccessCloserError('');
      setSuccessCloserShowTagger(true);
      setSuccessCloserShowMainEntity(true);
      setSuccessCloserTxDialog(null);
      setByDateRange(createAllTimeRange());
      setByDateRows([]);
      setByDateUniqueSummary(EMPTY_BY_DATE_SUMMARY);
      setByDateLoading(false);
      setByDateError('');
      setByDateHasSearched(false);
      setByDateResultTab('activityAndTransaction');
      setByDateSearch('');
      setByDateSortField('qualifyingTransactionCount');
      setByDateSortDir('desc');
      setByDateActivityDialog(null);
      setByDateActivityLoading(false);
      setByDateActivityError('');
      setByDateActivityUsage(null);
      setByDateTxDialog(null);
      setByDateTxLoading(false);
      setByDateTxError('');
      setByDateTxUnits([]);
      setByDateQualifyingTxDialog(null);
      setByDateQualifyingTxLoading(false);
      setByDateQualifyingTxError('');
      setByDateQualifyingTxList([]);
      setByDateQualifyingActivityDialog(null);
      setByDateQualifyingActivityLoading(false);
      setByDateQualifyingActivityError('');
      setByDateQualifyingActivityList([]);
      setByDateAllTxDialog(null);
      setByDateAllTxLoading(false);
      setByDateAllTxError('');
      setByDateAllTxList([]);
      setByDateAllActivityDialogOpen(false);
      setByDateAllAgentsDialogOpen(false);
      setSoldUnitsDialogContext(null);
      setSoldUnits([]);
      setSoldUnitsLoading(false);
      setJyUsersRange(createAllTimeRange());
      setJyUsersRows([]);
      setJyUsersLoading(false);
      setJyUsersError('');
      setJyUsersHasSearched(false);
      setJyUsersSearch('');
      setJyUsersUsageFilter('all');
      setJyUsersSortField('activityCount');
      setJyUsersSortDir('desc');
    }
  }, [open]);

  const handleDrillDownSort = useCallback((field: ProjectTabSortField) => {
    setDrillDownSortDir((prev) => (drillDownSortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'));
    setDrillDownSortField(field);
  }, [drillDownSortField]);

  const filteredSortedDrillDownUsers = useMemo(() => {
    let rows = drillDownTopUsers;
    if (drillDownFilter) {
      const lower = drillDownFilter.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.ceaNumber.toLowerCase().includes(lower) ||
          (r.name || '').toLowerCase().includes(lower) ||
          (r.mobile || '').toLowerCase().includes(lower)
      );
    }
    if (drillDownRemoveConvertedAgents) {
      rows = rows.filter((r) => !convertedAgentCeaSet.has(r.ceaNumber.trim().toUpperCase()));
    }
    if (drillDownHideNonZeroCloseNl) {
      rows = rows.filter((r) => (r.closeNewLaunchCount || 0) === 0);
    }
    if (drillDownOnlyResaleAgent) {
      rows = rows.filter((r) => Boolean(r.isResaleAgent));
    }
    if (drillDownHideZeroViewsAllTime) {
      rows = rows.filter((r) => r.viewsAllTime !== 0);
    }
    if (drillDownHideZeroViewsPeriod) {
      rows = rows.filter((r) => r.viewsPeriod !== 0);
    }
    if (drillDownHideZeroSoldAllTime) {
      rows = rows.filter((r) => r.soldAllTime !== 0);
    }
    if (drillDownHideZeroSoldPeriod) {
      rows = rows.filter((r) => r.soldPeriod !== 0);
    }
    const sorted = [...rows].sort((a, b) => {
      let cmp = 0;
      switch (drillDownSortField) {
        case 'ceaNumber':
          cmp = a.ceaNumber.localeCompare(b.ceaNumber);
          break;
        case 'name':
          cmp = (a.name || '').localeCompare(b.name || '');
          break;
        case 'mobile':
          cmp = (a.mobile || '').localeCompare(b.mobile || '');
          break;
        case 'viewsAllTime':
          cmp = a.viewsAllTime - b.viewsAllTime;
          break;
        case 'viewsPeriod':
          cmp = a.viewsPeriod - b.viewsPeriod;
          break;
        case 'soldAllTime':
          cmp = a.soldAllTime - b.soldAllTime;
          break;
        case 'soldPeriod':
          cmp = a.soldPeriod - b.soldPeriod;
          break;
        case 'closeNewLaunchCount':
          cmp = (a.closeNewLaunchCount || 0) - (b.closeNewLaunchCount || 0);
          break;
      }
      return drillDownSortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [
    drillDownTopUsers,
    drillDownFilter,
    drillDownRemoveConvertedAgents,
    convertedAgentCeaSet,
    drillDownHideNonZeroCloseNl,
    drillDownOnlyResaleAgent,
    drillDownHideZeroViewsAllTime,
    drillDownHideZeroViewsPeriod,
    drillDownHideZeroSoldAllTime,
    drillDownHideZeroSoldPeriod,
    drillDownSortField,
    drillDownSortDir,
  ]);

  const drillDownViewsPeriodByCea = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of drillDownTopUsers) {
      if (row.ceaNumber) map.set(row.ceaNumber, row.viewsPeriod || 0);
    }
    return map;
  }, [drillDownTopUsers]);

  const handleDrillDownEoiSort = useCallback((field: DrillDownEoiSortField) => {
    setDrillDownEoiSortDir((prev) => (drillDownEoiSortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'));
    setDrillDownEoiSortField(field);
  }, [drillDownEoiSortField]);

  const filteredSortedDrillDownEois = useMemo(() => {
    let rows = drillDownEois.map((r) => ({
      ...r,
      viewsPeriod: drillDownViewsPeriodByCea.get(r.ceaNumber) ?? 0,
    }));
    if (drillDownFilter) {
      const lower = drillDownFilter.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.ceaNumber.toLowerCase().includes(lower) ||
          (r.name || '').toLowerCase().includes(lower) ||
          (r.mobile || '').toLowerCase().includes(lower)
      );
    }
    if (drillDownRemoveConvertedAgents) {
      rows = rows.filter((r) => !convertedAgentCeaSet.has(r.ceaNumber.trim().toUpperCase()));
    }
    if (drillDownHideNonZeroCloseNl) {
      rows = rows.filter((r) => (r.closeNewLaunchCount || 0) === 0);
    }
    const sorted = [...rows].sort((a, b) => {
      let cmp = 0;
      switch (drillDownEoiSortField) {
        case 'ceaNumber':
          cmp = a.ceaNumber.localeCompare(b.ceaNumber);
          break;
        case 'name':
          cmp = (a.name || '').localeCompare(b.name || '');
          break;
        case 'mobile':
          cmp = (a.mobile || '').localeCompare(b.mobile || '');
          break;
        case 'viewsPeriod':
          cmp = a.viewsPeriod - b.viewsPeriod;
          break;
        case 'eoiCount':
          cmp = a.eoiCount - b.eoiCount;
          break;
        case 'closeNewLaunchCount':
          cmp = (a.closeNewLaunchCount || 0) - (b.closeNewLaunchCount || 0);
          break;
      }
      return drillDownEoiSortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [
    drillDownEois,
    drillDownViewsPeriodByCea,
    drillDownFilter,
    drillDownRemoveConvertedAgents,
    convertedAgentCeaSet,
    drillDownHideNonZeroCloseNl,
    drillDownEoiSortField,
    drillDownEoiSortDir,
  ]);

  // Read from the loaded payload (not the toggle) so chart labels always describe the data on screen,
  // including against a backend that predates the granularity parameter.
  const isMonthlyOverview = overviewData?.granularity === 'month';

  const topProject = useMemo<AnalyticsProjectStat | null>(() => {
    if (!overviewData || overviewData.topProjects.length === 0) {
      return null;
    }
    return overviewData.topProjects[0];
  }, [overviewData]);

  const overviewUsersSeries = useMemo(() => {
    const uniqueSeries = overviewData?.uniqueUserSeries ?? [];
    const newUserSeries = overviewData?.signUpSeries ?? [];
    const byLabel = new Map<string, { label: string; uniqueUsers: number; newUsers: number }>();

    for (const point of uniqueSeries) {
      byLabel.set(point.label, {
        label: point.label,
        uniqueUsers: Number(point.users || 0),
        newUsers: byLabel.get(point.label)?.newUsers ?? 0,
      });
    }

    for (const point of newUserSeries) {
      const existing = byLabel.get(point.label);
      byLabel.set(point.label, {
        label: point.label,
        uniqueUsers: existing?.uniqueUsers ?? 0,
        newUsers: Number(point.users || 0),
      });
    }

    const ordered = Array.from(byLabel.values()).sort((a, b) => a.label.localeCompare(b.label));
    let runningNewUsers = 0;

    return ordered.map((point) => {
      runningNewUsers += point.newUsers;
      return {
        ...point,
        cumulativeNewUsers: runningNewUsers,
      };
    });
  }, [overviewData]);

  const overviewActivitySeries = useMemo(() => {
    const series = overviewData?.activitySeries ?? [];
    const ordered = [...series].sort((a, b) => a.label.localeCompare(b.label));
    let running = 0;
    return ordered.map((point) => {
      const activities = Number(point.activities || 0);
      running += activities;
      return {
        label: point.label,
        activities,
        cumulativeActivities: running,
      };
    });
  }, [overviewData]);


  const userEventUsage = userData?.userUsage?.eventUsage ?? [];
  const userProjectViews = userData?.userUsage?.projectViews ?? [];

  const materialEngagementLineData = useMemo(() => {
    const series = materialEngagementData?.series ?? [];
    return series.map((pt) => ({
      date: pt.date,
      spEdmPreviews: pt.spEdmPreviews,
      spEdmDownloads: pt.spEdmDownloads,
      spEbookPreviews: pt.spEbookPreviews,
      spEbookDownloads: pt.spEbookDownloads,
      peEdmPreviews: pt.peEdmPreviews,
      peEdmDownloads: pt.peEdmDownloads,
      peEbookPreviews: pt.peEbookPreviews,
      peEbookDownloads: pt.peEbookDownloads,
      totalEdmPreviews: pt.spEdmPreviews + pt.peEdmPreviews,
      totalEdmDownloads: pt.spEdmDownloads + pt.peEdmDownloads,
      totalEbookPreviews: pt.spEbookPreviews + pt.peEbookPreviews,
      totalEbookDownloads: pt.spEbookDownloads + pt.peEbookDownloads,
    }));
  }, [materialEngagementData]);

  const materialEngagementTopMaterialsData = useMemo(() => {
    const all = materialAppSource === 'super_picker'
      ? materialEngagementData?.topMaterialsSp ?? []
      : materialAppSource === 'project_essential'
        ? materialEngagementData?.topMaterialsPe ?? []
        : materialEngagementData?.topMaterials ?? [];
    const edms = all.filter((m: MaterialEngagementTopMaterial) => m.type === 'edm').slice(0, 10);
    const ebooks = all.filter((m: MaterialEngagementTopMaterial) => m.type === 'ebook').slice(0, 10);
    return { edms, ebooks };
  }, [materialEngagementData, materialAppSource]);

  const materialEngagementTopProjectsData = useMemo(() => {
    const topProjects = materialAppSource === 'super_picker'
      ? materialEngagementData?.topProjectsSp ?? []
      : materialAppSource === 'project_essential'
        ? materialEngagementData?.topProjectsPe ?? []
        : materialEngagementData?.topProjects ?? [];
    return topProjects.slice(0, 10).map((p) => ({
      name: p.projectName.length > 20 ? `${p.projectName.slice(0, 19)}…` : p.projectName,
      fullName: p.projectName,
      count: p.totalInteractions,
    }));
  }, [materialEngagementData, materialAppSource]);

  const userSoldUnitsWithTransactionRole = useMemo(
    () => dedupeSoldUnitTransactions(userAllSoldUnits),
    [userAllSoldUnits]
  );
  const byDateResultTabs: Array<{ value: ByDateResultTab; label: string }> = useMemo(() => ([
    { value: 'activityAndTransaction', label: 'Has Activity + Got Tx' },
    { value: 'noActivityNoTransaction', label: 'No Activity + No Tx' },
    { value: 'activityNoTransaction', label: 'Has Activity + No Tx' },
    { value: 'noActivityTransaction', label: 'No Activity + Got Tx' },
  ]), []);
  const byDateSearchKeyword = byDateSearch.trim().toLowerCase();
  const matchesByDateSearch = useCallback((row: AdminByDateAnalyticsRow) => {
    if (!byDateSearchKeyword) {
      return true;
    }
    const name = (row.name || '').toLowerCase();
    const cea = (row.ceaNumber || '').toLowerCase();
    const mobile = (row.mobile || '').toLowerCase();
    const email = (row.email || '').toLowerCase();
    return (
      name.includes(byDateSearchKeyword)
      || cea.includes(byDateSearchKeyword)
      || mobile.includes(byDateSearchKeyword)
      || email.includes(byDateSearchKeyword)
    );
  }, [byDateSearchKeyword]);
  const byDateFilteredBuckets = useMemo<Record<ByDateResultTab, AdminByDateAnalyticsRow[]>>(() => {
    return {
      activityAndTransaction: byDateBuckets.activityAndTransaction.filter(matchesByDateSearch),
      noActivityNoTransaction: byDateBuckets.noActivityNoTransaction.filter(matchesByDateSearch),
      activityNoTransaction: byDateBuckets.activityNoTransaction.filter(matchesByDateSearch),
      noActivityTransaction: byDateBuckets.noActivityTransaction.filter(matchesByDateSearch),
    };
  }, [byDateBuckets, matchesByDateSearch]);
  const activeByDateRows = byDateFilteredBuckets[byDateResultTab];
  const filteredActiveByDateRows = activeByDateRows;
  const sortedFilteredActiveByDateRows = useMemo(() => {
    const rows = [...filteredActiveByDateRows];
    rows.sort((a, b) => {
      const compareString = (left: string, right: string): number => (
        left.localeCompare(right, undefined, { sensitivity: 'base', numeric: true })
      );

      let cmp = 0;
      switch (byDateSortField) {
        case 'name':
          cmp = compareString((a.name || '').trim(), (b.name || '').trim());
          break;
        case 'ceaNumber':
          cmp = compareString((a.ceaNumber || '').trim(), (b.ceaNumber || '').trim());
          break;
        case 'mobile':
          cmp = compareString((a.mobile || '').trim(), (b.mobile || '').trim());
          break;
        case 'email':
          cmp = compareString((a.email || '').trim(), (b.email || '').trim());
          break;
        case 'activityCount':
          cmp = Number(a.activityCount || 0) - Number(b.activityCount || 0);
          break;
        case 'transactionCount':
          cmp = Number(a.transactionCount || 0) - Number(b.transactionCount || 0);
          break;
        case 'qualifyingTransactionCount':
          cmp = Number(a.qualifyingTransactionCount || 0) - Number(b.qualifyingTransactionCount || 0);
          break;
        case 'qualifyingActivityCount':
          cmp = Number(a.qualifyingActivityCount || 0) - Number(b.qualifyingActivityCount || 0);
          break;
      }
      return byDateSortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [filteredActiveByDateRows, byDateSortDir, byDateSortField]);
  const byDateActivityEventUsage = byDateActivityUsage?.eventUsage ?? [];
  const byDateActivityProjectViews = byDateActivityUsage?.projectViews ?? [];

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
          minHeight: '78vh',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography variant="h6">Analytics Dashboard</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)' }}>
              Admin analytics with overview and user drill-down.
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={2.5}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value as AnalyticsTab)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)',
              '& .MuiTab-root': { color: 'rgba(var(--color-text-rgb), 0.68)', minHeight: 44 },
              '& .Mui-selected': { color: 'var(--color-accent) !important' },
              '& .MuiTabs-indicator': { backgroundColor: 'var(--color-accent)' },
            }}
          >
            <Tab label="Main Overview" value="byDate" />
            <Tab label="Overview" value="overview" />
            <Tab label="By User" value="user" />
            <Tab label="By Project" value="project" />
            <Tab label="Success Closer" value="successCloser" />
            <Tab label="Shareable Links" value="shareLinks" />
            <Tab label="JY Users" value="jyUsers" />
            <Tab label="Check User" value="checkUser" />
            <Tab label="Token Usage" value="tokenUsage" />
          </Tabs>

          {activeTab === 'overview' ? (
            <>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <DateRangeSelector
                  label="Overview Range"
                  range={overviewRange}
                  onApply={setOverviewRange}
                />
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={overviewGranularity}
                  onChange={(_event, value: OverviewGranularity | null) => {
                    if (!value) {
                      return;
                    }
                    setOverviewGranularity(value);
                    if (value === 'month') {
                      // The per-month comparison is the point of this view — show it without an extra click.
                      setUsersOverTimeOpen(true);
                    }
                  }}
                  aria-label="Series granularity"
                  sx={{
                    height: 40,
                    '& .MuiToggleButton-root': {
                      px: 2,
                      textTransform: 'none',
                      borderColor: 'rgba(var(--color-text-rgb), 0.18)',
                      color: 'rgba(var(--color-text-rgb), 0.7)',
                    },
                    '& .MuiToggleButton-root.Mui-selected': {
                      color: 'var(--color-accent)',
                      bgcolor: 'rgba(237,137,54,0.14)',
                      '&:hover': { bgcolor: 'rgba(237,137,54,0.2)' },
                    },
                  }}
                >
                  <ToggleButton value="day" aria-label="Daily buckets">
                    Daily
                  </ToggleButton>
                  <ToggleButton value="month" aria-label="Month on month buckets">
                    Month on Month
                  </ToggleButton>
                </ToggleButtonGroup>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => void fetchOverview()}
                  sx={{ borderColor: 'rgba(var(--color-text-rgb), 0.18)', color: 'rgba(var(--color-text-rgb), 0.88)' }}
                >
                  Refresh
                </Button>
              </Box>

              {isMonthlyOverview ? (
                <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                  One bar per month. Cumulative bars carry the running total through each month, so the last
                  month matches the range total. Monthly unique users are distinct agents within that month,
                  and the first and last month count only the days inside the selected range.
                </Typography>
              ) : null}

              {overviewError ? <Alert severity="error">{overviewError}</Alert> : null}

              {overviewData?.warnings?.map((warning) => (
                <Alert key={`overview-${warning}`} severity="warning">{warning}</Alert>
              ))}

              {overviewLoading ? (
                <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Stack spacing={2.5}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Card sx={chartCardSx}>
                        <CardContent sx={{ p: '0 !important' }}>
                          <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                            Unique Users
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                            {formatCount(overviewData?.uniqueUsers)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mt: 1 }}>
                            Active users in the selected period.
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Card sx={chartCardSx}>
                        <CardContent sx={{ p: '0 !important' }}>
                          <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                            New Users
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                            {formatCount(overviewData?.newUsers)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mt: 1 }}>
                            First-time users in the selected period.
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Card
                        sx={{
                          ...chartCardSx,
                          background: 'linear-gradient(135deg, rgba(237,137,54,0.18), rgba(237,137,54,0.04))',
                          borderColor: 'rgba(237,137,54,0.18)',
                        }}
                      >
                        <CardContent sx={{ p: '0 !important' }}>
                          <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                            Top Project
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.75 }}>
                            {topProject?.projectName || 'No data'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)', mt: 1 }}>
                            {topProject ? `${formatCount(topProject.views)} tracked interactions` : 'No project activity in this period.'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sx={{ minWidth: 0 }}>
                      <Box sx={chartCardSx}>
                        <Box
                          role="button"
                          tabIndex={0}
                          onClick={() => setUsersOverTimeOpen((open) => !open)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setUsersOverTimeOpen((open) => !open);
                            }
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            userSelect: 'none',
                            mb: usersOverTimeOpen ? 2 : 0,
                            '&:focus-visible': { outline: '2px solid var(--color-accent)', outlineOffset: 2 },
                          }}
                          aria-expanded={usersOverTimeOpen}
                        >
                          {usersOverTimeOpen ? (
                            <ExpandLessIcon sx={{ fontSize: 20, color: 'rgba(var(--color-text-rgb), 0.75)' }} />
                          ) : (
                            <ExpandMoreIcon sx={{ fontSize: 20, color: 'rgba(var(--color-text-rgb), 0.75)' }} />
                          )}
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Users Over Time
                          </Typography>
                        </Box>
                        <Collapse in={usersOverTimeOpen} unmountOnExit>
                          <Box sx={{ width: '100%', height: 300, minWidth: 0 }}>
                            <ResponsiveContainer width="100%" height={300}>
                              <ComposedChart data={overviewUsersSeries}>
                                <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" />
                                <XAxis
                                  dataKey="label"
                                  stroke={chartAxisStroke}
                                  tick={chartTickStyle}
                                  tickFormatter={formatSeriesLabel}
                                />
                                <YAxis allowDecimals={false} stroke={chartAxisStroke} tick={chartTickStyle} />
                                <RechartsTooltip
                                  content={<AnalyticsChartTooltip />}
                                  cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                                />
                                <Bar
                                  dataKey="newUsers"
                                  name="New Users"
                                  fill="#4299E1"
                                  radius={[6, 6, 0, 0]}
                                  barSize={isMonthlyOverview ? 40 : 22}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="uniqueUsers"
                                  name="Unique Users"
                                  stroke="var(--color-accent)"
                                  strokeWidth={3}
                                  dot={{ r: 3 }}
                                  activeDot={{ r: 5 }}
                                />
                              </ComposedChart>
                            </ResponsiveContainer>
                          </Box>
                        </Collapse>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sx={{ minWidth: 0 }}>
                      <Box sx={chartCardSx}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                          Cumulative New Users
                        </Typography>
                        <Box sx={{ width: '100%', height: 300, minWidth: 0 }}>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={overviewUsersSeries}>
                              <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" />
                              <XAxis
                                dataKey="label"
                                stroke={chartAxisStroke}
                                tick={chartTickStyle}
                                tickFormatter={formatSeriesLabel}
                              />
                              <YAxis allowDecimals={false} stroke={chartAxisStroke} tick={chartTickStyle} />
                              <RechartsTooltip
                                content={<AnalyticsChartTooltip />}
                                cursor={{ fill: 'rgba(99, 179, 237, 0.14)' }}
                              />
                              <Bar
                                dataKey="cumulativeNewUsers"
                                name="Cumulative New Users"
                                fill="#63B3ED"
                                radius={[6, 6, 0, 0]}
                                barSize={isMonthlyOverview ? 40 : undefined}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sx={{ minWidth: 0 }}>
                      <Box sx={chartCardSx}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                          Cumulative Activities
                        </Typography>
                        <Box sx={{ width: '100%', height: 300, minWidth: 0 }}>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                              data={overviewActivitySeries}
                              margin={{ top: 6, right: 8, left: 8, bottom: 6 }}
                            >
                              <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" />
                              <XAxis
                                dataKey="label"
                                stroke={chartAxisStroke}
                                tick={chartTickStyle}
                                tickFormatter={formatSeriesLabel}
                              />
                              <YAxis allowDecimals={false} stroke={chartAxisStroke} tick={chartTickStyle} />
                              <RechartsTooltip
                                content={<AnalyticsChartTooltip />}
                                cursor={{ fill: 'rgba(237, 137, 54, 0.14)' }}
                              />
                              <Bar
                                dataKey="cumulativeActivities"
                                name="Cumulative Activities"
                                fill="#ED8936"
                                radius={[6, 6, 0, 0]}
                                barSize={isMonthlyOverview ? 40 : undefined}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box sx={chartCardSx}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Top Viewed Projects
                    </Typography>
                    <Box sx={{ width: '100%', height: 380 }}>
                      <ResponsiveContainer>
                        <BarChart data={overviewData?.topProjects ?? []} layout="vertical" margin={{ left: 12, right: 12 }}>
                          <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" />
                          <XAxis type="number" allowDecimals={false} stroke={chartAxisStroke} tick={chartTickStyle} />
                          <YAxis
                            type="category"
                            dataKey="projectName"
                            width={160}
                            stroke={chartAxisStroke}
                            tick={chartTickStyle}
                            tickFormatter={formatProjectLabel}
                          />
                          <RechartsTooltip />
                          <Bar
                            dataKey="views"
                            radius={[0, 6, 6, 0]}
                            onClick={(e: { payload?: AnalyticsProjectStat }) => {
                              if (e?.payload) handleBarClick(e.payload);
                            }}
                            cursor="pointer"
                          >
                            {(overviewData?.topProjects ?? []).map((entry, index) => (
                              <Cell key={`${entry.projectName}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                </Stack>
              )}
            </>
          ) : activeTab === 'user' ? (
            <>
              {/* 1) CEA search */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                <TextField
                  size="small"
                  label="CEA Number"
                  value={draftUserCea}
                  onChange={(event) => setDraftUserCea(event.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void fetchUserAnalytics(draftUserCea); }}
                  placeholder="Enter CEA number"
                  sx={{ minWidth: 240, '& .MuiInputBase-input': { color: 'var(--color-text-strong)' } }}
                />
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={() => void fetchUserAnalytics(draftUserCea)}
                  sx={{ bgcolor: 'var(--color-accent)', '&:hover': { bgcolor: 'var(--color-accent-strong)' } }}
                >
                  Search
                </Button>
              </Box>

              {userError ? <Alert severity="error">{userError}</Alert> : null}
              {userData?.warnings?.map((warning) => (
                <Alert key={`user-${warning}`} severity="warning">{warning}</Alert>
              ))}

              {(userLoading || userProfileLoading) ? (
                <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : !appliedUserCea ? (
                <Box
                  sx={{
                    py: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 1.5,
                    border: '1px dashed rgba(var(--color-text-rgb), 0.14)',
                    bgcolor: 'rgba(var(--color-text-rgb), 0.02)',
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                    Enter a CEA number above to view agent profile, transactions, and usage analytics.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2.5}>
                  {/* 2) Agent Profile */}
                  <Card sx={chartCardSx}>
                    <CardContent sx={{ p: '0 !important' }}>
                      {userProfileLoading ? (
                        <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Box>
                            <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.5)' }}>
                              Agent Profile
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                              {userProfile?.name || appliedUserCea}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <Box>
                              <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.5)' }}>CEA Number</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{appliedUserCea}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.5)' }}>Mobile</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{userProfile?.mobile || '—'}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.5)' }}>Email</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{userProfile?.email || '—'}</Typography>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>

                  {/* 3) New Sale Transaction — all sold units */}
                  <Box sx={chartCardSx}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                      New Sale Transactions ({userSoldUnitsWithTransactionRole.length})
                    </Typography>
                    {userProfileLoading ? (
                      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={28} />
                      </Box>
                    ) : userSoldUnitsWithTransactionRole.length === 0 ? (
                      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                        No transactions found for this agent.
                      </Typography>
                    ) : (
                      <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)', maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)', bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>Transaction No</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)', bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>Project</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)', bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>Unit No</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)', bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>Option Date</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)', bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>Transacted Price</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)', bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>Transaction Role</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)', bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>Picker Activity</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {userSoldUnitsWithTransactionRole.map((unit) => {
                              const activityCount = userProjectViews.find(
                                (pv) => pv.projectName.toUpperCase() === (unit.reference || '').toUpperCase()
                              )?.views;
                              return (
                                <TableRow key={`${unit.transactionNo}-${unit.reference}-${unit.unitNo}-${unit.optionDate}`}>
                                  <TableCell>{unit.transactionNo}</TableCell>
                                  <TableCell>{unit.reference}</TableCell>
                                  <TableCell>{unit.unitNo}</TableCell>
                                  <TableCell>{unit.optionDate}</TableCell>
                                  <TableCell align="right">
                                    {unit.transactionPrice.toLocaleString('en-SG', { style: 'currency', currency: 'SGD', minimumFractionDigits: 0 })}
                                  </TableCell>
                                  <TableCell>{unit.transactionRole}</TableCell>
                                  <TableCell align="right">
                                    {activityCount != null ? (
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#38B2AC' }}>
                                        {formatCount(activityCount)}
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.45)' }}>—</Typography>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>

                  {/* 4) User Usage on Picker */}
                  <Box sx={chartCardSx}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        User Usage on Picker
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
                        <DateRangeSelector
                          label="Usage Range"
                          range={userRange}
                          onApply={(r) => { setUserRange(r); }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<RefreshIcon />}
                          onClick={() => void fetchUserAnalytics()}
                          sx={{ borderColor: 'rgba(var(--color-text-rgb), 0.18)', color: 'rgba(var(--color-text-rgb), 0.88)' }}
                        >
                          Refresh
                        </Button>
                      </Box>
                    </Box>

                    {userLoading ? (
                      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={28} />
                      </Box>
                    ) : !userData?.userUsage ? (
                      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                        No usage data found for this period.
                      </Typography>
                    ) : (
                      <Grid container spacing={2.5}>
                        {/* Left: Event breakdown bar chart */}
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mb: 1.5 }}>
                            Event breakdown ({formatCount(userData.userUsage.totalEvents)} total events)
                          </Typography>
                          <Box sx={{ width: '100%', height: 320 }}>
                            <ResponsiveContainer>
                              <BarChart data={userEventUsage}>
                                <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" />
                                <XAxis dataKey="eventName" stroke={chartAxisStroke} tick={chartTickStyleSmall} angle={-30} textAnchor="end" height={60} />
                                <YAxis allowDecimals={false} stroke={chartAxisStroke} tick={chartTickStyle} />
                                <RechartsTooltip />
                                <Bar dataKey="count" fill="#38B2AC" radius={[6, 6, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        </Grid>

                        {/* Right: User Project Views with sold count */}
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mb: 1.5 }}>
                            Project Views ({userProjectViews.length})
                          </Typography>
                          {userProjectViews.length > 0 ? (
                            <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)', maxHeight: 320 }}>
                              <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)', bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>Project</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)', bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>Activity</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)', bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>Sold</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {userProjectViews.map((item) => {
                                    const soldCount = userProjectSoldCounts[item.projectName.toUpperCase()] || 0;
                                    return (
                                      <TableRow key={item.projectName}>
                                        <TableCell sx={{ maxWidth: 200, wordBreak: 'break-word' }}>{item.projectName}</TableCell>
                                        <TableCell align="right">{formatCount(item.views)}</TableCell>
                                        <TableCell align="right">
                                          {soldCount > 0 ? (
                                            <Button
                                              size="small"
                                              variant="text"
                                              sx={{ minWidth: 'auto', p: 0, textTransform: 'none', fontWeight: 600, color: '#4299E1' }}
                                              onClick={() => handleUserProjectSoldClick(item.projectName)}
                                            >
                                              {soldCount}
                                            </Button>
                                          ) : (
                                            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.45)' }}>0</Typography>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          ) : (
                            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                              No project views recorded in this period.
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    )}
                  </Box>

                  {/* 5) Material Engagement Charts */}
                  <Box sx={chartCardSx}>
                    <MaterialAnalyticsToolbar
                      title="Material Engagement"
                      materialAppSource={materialAppSource}
                      onMaterialAppSourceChange={setMaterialAppSource}
                      materialType={materialType}
                      onMaterialTypeChange={setMaterialType}
                      onRefresh={() => void fetchMaterialEngagement()}
                    />

                    {materialEngagementError ? (
                      <Alert severity="error" sx={{ mb: 2 }}>{materialEngagementError}</Alert>
                    ) : null}

                    {materialEngagementLoading ? (
                      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={28} />
                      </Box>
                    ) : (
                      <>
                      <Grid container spacing={2.5}>
                        {/* Line chart */}
                        <Grid item xs={12} md={7}>
                          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mb: 1.5 }}>
                            {`${materialType === 'edm' ? 'EDM' : 'eBook'} previews & downloads`}
                          </Typography>
                          {materialEngagementLineData.length === 0 ? (
                            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.45)' }}>
                              No data for this period.
                            </Typography>
                          ) : (
                            <Box sx={{ width: '100%', height: 300 }}>
                              <ResponsiveContainer>
                                <BarChart data={materialEngagementLineData} barCategoryGap="30%">
                                  <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" vertical={false} />
                                  <XAxis dataKey="date" stroke={chartAxisStroke} tick={chartTickStyleSmall} angle={-30} textAnchor="end" height={60} />
                                  <YAxis allowDecimals={false} stroke={chartAxisStroke} tick={chartTickStyle} />
                                  <RechartsTooltip />
                                  <Legend wrapperStyle={{ fontSize: '0.75rem', paddingTop: 8 }} />
                                  {materialType === 'edm' ? (
                                    materialAppSource === 'all' ? (
                                      <>
                                        <Bar dataKey="spEdmPreviews" name="SP EDM Previews" stackId="preview" fill="#ED8936" />
                                        <Bar dataKey="peEdmPreviews" name="PE EDM Previews" stackId="preview" fill="#38B2AC" radius={[3, 3, 0, 0]} />
                                        <Bar dataKey="spEdmDownloads" name="SP EDM Downloads" stackId="download" fill="#F6AD55" />
                                        <Bar dataKey="peEdmDownloads" name="PE EDM Downloads" stackId="download" fill="#4299E1" radius={[3, 3, 0, 0]} />
                                        <Bar dataKey="totalEdmPreviews" name="Total Previews" stackId="total" fill="#ECC94B" />
                                        <Bar dataKey="totalEdmDownloads" name="Total Downloads" stackId="total" fill="#63B3ED" radius={[3, 3, 0, 0]} />
                                      </>
                                    ) : materialAppSource === 'super_picker' ? (
                                      <>
                                        <Bar dataKey="spEdmPreviews" name="EDM Previews" stackId="preview" fill="#ED8936" radius={[3, 3, 0, 0]} />
                                        <Bar dataKey="spEdmDownloads" name="EDM Downloads" stackId="download" fill="#F6AD55" radius={[3, 3, 0, 0]} />
                                      </>
                                    ) : (
                                      <>
                                        <Bar dataKey="peEdmPreviews" name="EDM Previews" stackId="preview" fill="#38B2AC" radius={[3, 3, 0, 0]} />
                                        <Bar dataKey="peEdmDownloads" name="EDM Downloads" stackId="download" fill="#4299E1" radius={[3, 3, 0, 0]} />
                                      </>
                                    )
                                  ) : materialAppSource === 'all' ? (
                                    <>
                                      <Bar dataKey="spEbookPreviews" name="SP eBook Previews" stackId="preview" fill="#ED8936" />
                                      <Bar dataKey="peEbookPreviews" name="PE eBook Previews" stackId="preview" fill="#38B2AC" radius={[3, 3, 0, 0]} />
                                      <Bar dataKey="spEbookDownloads" name="SP eBook Downloads" stackId="download" fill="#F6AD55" />
                                      <Bar dataKey="peEbookDownloads" name="PE eBook Downloads" stackId="download" fill="#4299E1" radius={[3, 3, 0, 0]} />
                                      <Bar dataKey="totalEbookPreviews" name="Total Previews" stackId="total" fill="#ECC94B" />
                                      <Bar dataKey="totalEbookDownloads" name="Total Downloads" stackId="total" fill="#63B3ED" radius={[3, 3, 0, 0]} />
                                    </>
                                  ) : materialAppSource === 'super_picker' ? (
                                    <>
                                      <Bar dataKey="spEbookPreviews" name="eBook Previews" stackId="preview" fill="#ED8936" radius={[3, 3, 0, 0]} />
                                      <Bar dataKey="spEbookDownloads" name="eBook Downloads" stackId="download" fill="#F6AD55" radius={[3, 3, 0, 0]} />
                                    </>
                                  ) : (
                                    <>
                                      <Bar dataKey="peEbookPreviews" name="eBook Previews" stackId="preview" fill="#38B2AC" radius={[3, 3, 0, 0]} />
                                      <Bar dataKey="peEbookDownloads" name="eBook Downloads" stackId="download" fill="#4299E1" radius={[3, 3, 0, 0]} />
                                    </>
                                  )}
                                </BarChart>
                              </ResponsiveContainer>
                            </Box>
                          )}
                        </Grid>

                        {/* Top projects bar chart */}
                        <Grid item xs={12} md={5}>
                          <TopProjectsChartHeader />
                          {materialEngagementTopProjectsData.length === 0 ? (
                            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.45)' }}>
                              No project data for this period.
                            </Typography>
                          ) : (
                            <Box sx={{ width: '100%', height: 300 }}>
                              <ResponsiveContainer>
                                <BarChart data={materialEngagementTopProjectsData} layout="vertical" margin={{ left: 0, right: 24 }}>
                                  <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" horizontal={false} />
                                  <XAxis type="number" allowDecimals={false} stroke={chartAxisStroke} tick={chartTickStyle} />
                                  <YAxis type="category" dataKey="name" stroke={chartAxisStroke} tick={chartTickStyleSmall} width={110} />
                                  <RechartsTooltip
                                    formatter={(value: number) => [value, 'Interactions']}
                                    labelFormatter={(_label: string, payload: ReadonlyArray<{ payload?: { fullName?: string } }>) => payload?.[0]?.payload?.fullName ?? _label}
                                  />
                                  <Bar dataKey="count" name="Interactions" radius={[0, 4, 4, 0]}>
                                    {materialEngagementTopProjectsData.map((_entry, index) => (
                                      <Cell key={`mat-proj-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </Box>
                          )}
                        </Grid>
                      </Grid>

                      {/* Top EDMs and eBooks table */}
                      <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} md={6}>
                          <TopMaterialsList label="EDM" rows={materialEngagementTopMaterialsData.edms} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TopMaterialsList label="eBook" rows={materialEngagementTopMaterialsData.ebooks} />
                        </Grid>
                      </Grid>
                      </>
                    )}
                  </Box>
                </Stack>
              )}
            </>
          ) : activeTab === 'project' ? (
            <>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <DateRangeSelector
                  label="Project Range"
                  range={projectRange}
                  onApply={setProjectRange}
                />
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="project-select-label" shrink>Project</InputLabel>
                  <Select
                    labelId="project-select-label"
                    value={draftProject}
                    label="Project"
                    onChange={(e) => setDraftProject(e.target.value)}
                    displayEmpty
                    renderValue={(v) => (v ? formatProjectLabel(v) : 'Select a project')}
                    sx={{ color: 'var(--color-text-strong)' }}
                    disabled={projectListLoading}
                  >
                    <MenuItem value="">
                      <em>Select a project</em>
                    </MenuItem>
                    {projectList.map((p) => (
                      <MenuItem key={p.projectName} value={p.projectName}>
                        {formatProjectLabel(p.projectName)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={() => void fetchProjectAnalytics(draftProject)}
                  sx={{ bgcolor: 'var(--color-accent)', '&:hover': { bgcolor: 'var(--color-accent-strong)' } }}
                >
                  Apply Project
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => void fetchProjectList()}
                  sx={{ borderColor: 'rgba(var(--color-text-rgb), 0.18)', color: 'rgba(var(--color-text-rgb), 0.88)' }}
                >
                  Refresh
                </Button>
              </Box>

              {projectError ? <Alert severity="error">{projectError}</Alert> : null}

              {projectLoading ? (
                <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : !projectData ? (
                <Box
                  sx={{
                    py: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 1.5,
                    border: '1px dashed rgba(var(--color-text-rgb), 0.14)',
                    bgcolor: 'rgba(var(--color-text-rgb), 0.02)',
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                    Select a project and click Apply to view analytics.
                  </Typography>
                </Box>
              ) : (
                  <Stack spacing={2.5}>
                    <Card sx={chartCardSx}>
                      <CardContent sx={{ p: '0 !important' }}>
                        <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                          Current Filter
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.75 }}>
                          {appliedProject || 'No project selected'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mt: 1 }}>
                          Total activity: {formatCount(projectData.dailySeries.reduce((s, d) => s + d.count, 0))} events
                        </Typography>
                      </CardContent>
                    </Card>

                    <Tabs
                      value={projectMainSubTab}
                      onChange={(_, v) => setProjectMainSubTab(v as 'activity' | 'materials')}
                      sx={{
                        borderBottom: '1px solid rgba(var(--color-text-rgb), 0.12)',
                        minHeight: 40,
                        '& .MuiTab-root': { minHeight: 40, textTransform: 'none' },
                      }}
                    >
                      <Tab value="activity" label="Super Picker Activity" />
                      <Tab value="materials" label="Marketing Materials Activity" />
                    </Tabs>

                    {projectMainSubTab === 'activity' && (
                    <>
                    <Box sx={chartCardSx}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                        Activity by Day
                      </Typography>
                      <Box sx={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer>
                          <BarChart data={projectData.dailySeries}>
                            <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" />
                            <XAxis dataKey="label" stroke={chartAxisStroke} tick={chartTickStyle} />
                            <YAxis allowDecimals={false} stroke={chartAxisStroke} tick={chartTickStyle} />
                            <RechartsTooltip />
                            <Bar dataKey="count" fill="#4299E1" radius={[6, 6, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>

                    <Box sx={chartCardSx}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                        Activity by Event
                      </Typography>
                      <Box sx={{ width: '100%', height: 320 }}>
                        <ResponsiveContainer>
                          <BarChart data={projectData.eventBreakdown}>
                            <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" />
                            <XAxis dataKey="eventName" stroke={chartAxisStroke} tick={{ ...chartTickStyle, fontSize: 11 }} />
                            <YAxis allowDecimals={false} stroke={chartAxisStroke} tick={chartTickStyle} />
                            <RechartsTooltip />
                            <Bar dataKey="count" fill="#38B2AC" radius={[6, 6, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>

                    <Box sx={chartCardSx}>
                      <Tabs
                        value={projectSubTab}
                        onChange={(_e, val) => setProjectSubTab(val as ProjectTabSubTab)}
                        sx={{
                          mb: 2,
                          borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)',
                          '& .MuiTab-root': { textTransform: 'none', color: 'rgba(var(--color-text-rgb), 0.65)' },
                          '& .Mui-selected': { color: 'var(--color-text-strong) !important' },
                        }}
                      >
                        <Tab
                          value="top"
                          label={`All Users (${filteredSortedProjectTabUsers.length})`}
                        />
                        <Tab value="eoi" label={`EOI (${filteredSortedProjectTabEois.length})`} />
                      </Tabs>

                      {projectSubTab === 'eoi' && projectTabEoiSendError && (
                        <Alert
                          severity="error"
                          onClose={() => setProjectTabEoiSendError('')}
                          sx={{ mb: 1.5 }}
                        >
                          {projectTabEoiSendError}
                        </Alert>
                      )}

                      <Stack spacing={1} sx={{ mb: 1.5, width: 340 }}>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              size="small"
                              checked={projectTabRemoveConvertedAgents}
                              onChange={(e) => setProjectTabRemoveConvertedAgents(e.target.checked)}
                            />
                          )}
                          label="Remove Converted Agent"
                        />
                        <TextField
                          size="small"
                          placeholder="Filter by CEA, name, or mobile…"
                          value={projectTabFilter}
                          onChange={(e) => setProjectTabFilter(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18, color: 'rgba(var(--color-text-rgb), 0.45)' }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Stack>

                      {projectSubTab === 'top' ? (
                      filteredSortedProjectTabUsers.length > 0
                        || projectTabFilter
                        || projectTabHideZeroViewsAllTime
                        || projectTabHideZeroViewsPeriod
                        || projectTabHideZeroSoldAllTime
                        || projectTabHideZeroSoldPeriod
                        || projectTabRemoveConvertedAgents
                        || projectTabHideNonZeroCloseNl
                        || projectTabOnlyResaleAgent ? (
                        <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                {([
                                  { field: 'ceaNumber' as const, label: 'CEA Number' },
                                  { field: 'name' as const, label: 'Name' },
                                  { field: 'mobile' as const, label: 'Mobile' },
                                ] as const).map((col) => (
                                  <TableCell
                                    key={col.field}
                                    sx={{
                                      fontWeight: 600,
                                      color: 'rgba(var(--color-text-rgb), 0.85)',
                                      cursor: 'pointer',
                                      userSelect: 'none',
                                      '&:hover': { color: 'var(--color-text-strong)' },
                                    }}
                                    onClick={() => handleProjectTabSort(col.field)}
                                  >
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                      {col.label}
                                      {projectTabSortField === col.field && (
                                        projectTabSortDir === 'asc'
                                          ? <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                                          : <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                                      )}
                                    </Box>
                                  </TableCell>
                                ))}
                                <AnalyticsMetricTableHeadCell
                                  field="viewsAllTime"
                                  label="Views (all time)"
                                  hideZero={projectTabHideZeroViewsAllTime}
                                  onHideZeroChange={setProjectTabHideZeroViewsAllTime}
                                  sortField={projectTabSortField}
                                  sortDir={projectTabSortDir}
                                  onSort={handleProjectTabSort}
                                />
                                <AnalyticsMetricTableHeadCell
                                  field="viewsPeriod"
                                  label="Views (selected period)"
                                  hideZero={projectTabHideZeroViewsPeriod}
                                  onHideZeroChange={setProjectTabHideZeroViewsPeriod}
                                  sortField={projectTabSortField}
                                  sortDir={projectTabSortDir}
                                  onSort={handleProjectTabSort}
                                />
                                <AnalyticsMetricTableHeadCell
                                  field="soldAllTime"
                                  label="Sold (all time)"
                                  hideZero={projectTabHideZeroSoldAllTime}
                                  onHideZeroChange={setProjectTabHideZeroSoldAllTime}
                                  sortField={projectTabSortField}
                                  sortDir={projectTabSortDir}
                                  onSort={handleProjectTabSort}
                                />
                                <AnalyticsMetricTableHeadCell
                                  field="soldPeriod"
                                  label="Sold (selected period)"
                                  hideZero={projectTabHideZeroSoldPeriod}
                                  onHideZeroChange={setProjectTabHideZeroSoldPeriod}
                                  sortField={projectTabSortField}
                                  sortDir={projectTabSortDir}
                                  onSort={handleProjectTabSort}
                                />
                                <CloseNewLaunchTableHeadCell
                                  checked={projectTabHideNonZeroCloseNl}
                                  onCheckedChange={setProjectTabHideNonZeroCloseNl}
                                  sortField={projectTabSortField}
                                  sortDir={projectTabSortDir}
                                  onSort={() => handleProjectTabSort('closeNewLaunchCount')}
                                />
                                <ResaleAgentTableHeadCell
                                  checked={projectTabOnlyResaleAgent}
                                  onCheckedChange={setProjectTabOnlyResaleAgent}
                                />
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredSortedProjectTabUsers.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={9} sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                                    No results matching your filters.
                                  </TableCell>
                                </TableRow>
                              ) : (
                                filteredSortedProjectTabUsers
                                  .slice(
                                    projectTabUsersPage * PROJECT_TAB_USERS_PAGE_SIZE,
                                    (projectTabUsersPage + 1) * PROJECT_TAB_USERS_PAGE_SIZE,
                                  )
                                  .map((row) => (
                                    <TableRow key={row.ceaNumber}>
                                      <TableCell>{row.ceaNumber}</TableCell>
                                      <TableCell>{row.name?.trim() ? row.name : '—'}</TableCell>
                                      <TableCell>
                          {row.mobile?.trim() ? (
                            <a
                              href={`tel:${row.mobile}`}
                              style={{ color: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                            >
                              {row.mobile}
                            </a>
                          ) : '—'}
                        </TableCell>
                                      <TableCell align="right">{formatCount(row.viewsAllTime)}</TableCell>
                                      <TableCell align="right">{formatCount(row.viewsPeriod)}</TableCell>
                                      <TableCell align="right">
                                        {projectTabSoldRateLimited ? (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: 'rgba(var(--color-text-rgb), 0.65)',
                                              display: 'block',
                                              textAlign: 'right',
                                              maxWidth: 260,
                                              ml: 'auto',
                                              lineHeight: 1.35,
                                            }}
                                          >
                                            {SOLD_COLUMN_RATE_LIMIT_MESSAGE}
                                          </Typography>
                                        ) : row.soldAllTime > 0 ? (
                                          <Button
                                            size="small"
                                            variant="text"
                                            sx={{ minWidth: 'auto', p: 0, textTransform: 'none', fontWeight: 600, color: '#4299E1' }}
                                            onClick={() => handleProjectTabSoldClick(row.ceaNumber, 'allTime', row.name)}
                                          >
                                            {formatCount(row.soldAllTime)}
                                          </Button>
                                        ) : (
                                          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.45)' }}>0</Typography>
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        {projectTabSoldRateLimited ? (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: 'rgba(var(--color-text-rgb), 0.65)',
                                              display: 'block',
                                              textAlign: 'right',
                                              maxWidth: 260,
                                              ml: 'auto',
                                              lineHeight: 1.35,
                                            }}
                                          >
                                            {SOLD_COLUMN_RATE_LIMIT_MESSAGE}
                                          </Typography>
                                        ) : row.soldPeriod > 0 ? (
                                          <Button
                                            size="small"
                                            variant="text"
                                            sx={{ minWidth: 'auto', p: 0, textTransform: 'none', fontWeight: 600, color: '#4299E1' }}
                                            onClick={() => handleProjectTabSoldClick(row.ceaNumber, 'selectedPeriod', row.name)}
                                          >
                                            {formatCount(row.soldPeriod)}
                                          </Button>
                                        ) : (
                                          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.45)' }}>0</Typography>
                                        )}
                                      </TableCell>
                                      <TableCell align="right">{formatCount(row.closeNewLaunchCount || 0)}</TableCell>
                                      <TableCell align="right">
                                        {row.isResaleAgent ? (
                                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#48BB78' }}>Yes</Typography>
                                        ) : (
                                          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.45)' }}>No</Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))
                              )}
                            </TableBody>
                          </Table>
                          <TablePagination
                            component="div"
                            count={filteredSortedProjectTabUsers.length}
                            page={projectTabUsersPage}
                            onPageChange={(_, next) => setProjectTabUsersPage(next)}
                            rowsPerPage={PROJECT_TAB_USERS_PAGE_SIZE}
                            rowsPerPageOptions={[PROJECT_TAB_USERS_PAGE_SIZE]}
                            sx={{ color: 'rgba(var(--color-text-rgb), 0.75)' }}
                          />
                        </TableContainer>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                          No user activity recorded for this project in the selected period.
                        </Typography>
                      )
                      ) : (
                        projectTabEoisLoading ? (
                          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress size={32} />
                          </Box>
                        ) : (
                          <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  {([
                                    { field: 'ceaNumber' as const, label: 'CEA Number', align: 'left' as const },
                                    { field: 'name' as const, label: 'Name', align: 'left' as const },
                                    { field: 'mobile' as const, label: 'Mobile', align: 'left' as const },
                                    { field: 'viewsPeriod' as const, label: 'Views', align: 'right' as const },
                                    { field: 'eoiCount' as const, label: 'No. EOIs', align: 'right' as const },
                                  ] as const).map((col) => (
                                    <TableCell
                                      key={col.field}
                                      align={col.align}
                                      sx={{
                                        fontWeight: 600,
                                        color: 'rgba(var(--color-text-rgb), 0.85)',
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        '&:hover': { color: 'var(--color-text-strong)' },
                                      }}
                                      onClick={() => handleProjectTabEoiSort(col.field)}
                                    >
                                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                        {col.label}
                                        {projectTabEoiSortField === col.field && (
                                          projectTabEoiSortDir === 'asc'
                                            ? <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                                            : <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                                        )}
                                      </Box>
                                    </TableCell>
                                  ))}
                                  <CloseNewLaunchTableHeadCell
                                    checked={projectTabHideNonZeroCloseNl}
                                    onCheckedChange={setProjectTabHideNonZeroCloseNl}
                                    sortField={projectTabEoiSortField}
                                    sortDir={projectTabEoiSortDir}
                                    onSort={() => handleProjectTabEoiSort('closeNewLaunchCount')}
                                  />
                                  <TableCell
                                    align="center"
                                    sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}
                                  >
                                    Action
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {filteredSortedProjectTabEois.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={7} sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                                      {projectTabFilter || projectTabRemoveConvertedAgents || projectTabHideNonZeroCloseNl
                                        ? 'No results matching your filter.'
                                        : 'No EOIs registered for this project.'}
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  filteredSortedProjectTabEois.map((row) => {
                                    const sending = !!projectTabEoiSending[row.ceaNumber];
                                    const disabled = sending || !row.mobile?.trim();
                                    return (
                                      <TableRow key={row.ceaNumber}>
                                        <TableCell>{row.ceaNumber}</TableCell>
                                        <TableCell>{row.name?.trim() ? row.name : '—'}</TableCell>
                                        <TableCell>
                                          {row.mobile?.trim() ? (
                                            <a
                                              href={`tel:${row.mobile}`}
                                              style={{ color: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                                            >
                                              {row.mobile}
                                            </a>
                                          ) : '—'}
                                        </TableCell>
                                        <TableCell align="right">{formatCount(row.viewsPeriod)}</TableCell>
                                        <TableCell align="right">{formatCount(row.eoiCount)}</TableCell>
                                        <TableCell align="right">{formatCount(row.closeNewLaunchCount || 0)}</TableCell>
                                        <TableCell align="center">
                                          <Stack alignItems="center" spacing={0.5}>
                                            <Button
                                              size="small"
                                              variant="contained"
                                              disabled={disabled}
                                              onClick={() => void handleSendProjectTabEoiWhatsApp(row.ceaNumber)}
                                              startIcon={
                                                sending
                                                  ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
                                                  : <WhatsAppIcon sx={{ fontSize: 16 }} />
                                              }
                                              sx={{
                                                textTransform: 'none',
                                                bgcolor: '#25D366',
                                                color: '#fff',
                                                '&:hover': { bgcolor: '#1ebe57' },
                                                '&.Mui-disabled': { bgcolor: 'rgba(37, 211, 102, 0.35)', color: '#fff' },
                                                minWidth: 110,
                                              }}
                                            >
                                              {sending ? 'Sending…' : 'WhatsApp'}
                                            </Button>
                                            <Typography
                                              variant="caption"
                                              sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}
                                            >
                                              Engaged {row.engagedCount} time{row.engagedCount === 1 ? '' : 's'}
                                            </Typography>
                                          </Stack>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )
                      )}
                    </Box>
                    </>
                    )}

                    {projectMainSubTab === 'materials' && (
                      <ProjectMaterialsSubTab
                        loading={projectMaterialLoading}
                        error={projectMaterialError}
                        data={projectMaterialData}
                        metric={projectMaterialMetric}
                        materialAppSource={projectMaterialAppSource}
                        onMetricChange={setProjectMaterialMetric}
                        onMaterialAppSourceChange={setProjectMaterialAppSource}
                        onRefresh={fetchProjectMaterialAnalytics}
                        chartGridStroke={chartGridStroke}
                        chartAxisStroke={chartAxisStroke}
                        chartTickStyle={chartTickStyle}
                      />
                    )}
                  </Stack>
              )}
            </>
          ) : activeTab === 'byDate' ? (
            <>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <DateRangeSelector
                  label="Date Range"
                  range={byDateRange}
                  onApply={setByDateRange}
                />
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={() => void fetchByDate()}
                  sx={{ bgcolor: 'var(--color-accent)', '&:hover': { bgcolor: 'var(--color-accent-strong)' } }}
                >
                  Search
                </Button>
              </Box>

              {byDateError ? <Alert severity="error">{byDateError}</Alert> : null}

              {byDateLoading ? (
                <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : !byDateHasSearched ? (
                <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Stack spacing={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card
                        sx={{
                          ...chartCardSx,
                          cursor: byDateQualifyingSummary.transactedAmount > 0 ? 'pointer' : 'default',
                          transition: 'border-color 150ms',
                          '&:hover': byDateQualifyingSummary.transactedAmount > 0
                            ? { borderColor: 'var(--color-accent)' }
                            : undefined,
                        }}
                        onClick={() => {
                          if (byDateQualifyingSummary.transactedAmount > 0) {
                            void handleByDateAllQualifyingTxClick('amount');
                          }
                        }}
                      >
                        <CardContent sx={{ p: '0 !important' }}>
                          <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                            Transacted Amount
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                            {byDateQualifyingSummary.transactedAmount.toLocaleString('en-SG', {
                              style: 'currency',
                              currency: 'SGD',
                              maximumFractionDigits: 0,
                            })}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mt: 1 }}>
                            Sum of unique qualifying transactions.
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card
                        sx={{
                          ...chartCardSx,
                          cursor: byDateQualifyingSummary.transactionCount > 0 ? 'pointer' : 'default',
                          transition: 'border-color 150ms',
                          '&:hover': byDateQualifyingSummary.transactionCount > 0
                            ? { borderColor: 'var(--color-accent)' }
                            : undefined,
                        }}
                        onClick={() => {
                          if (byDateQualifyingSummary.transactionCount > 0) {
                            void handleByDateAllQualifyingTxClick('count');
                          }
                        }}
                      >
                        <CardContent sx={{ p: '0 !important' }}>
                          <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                            Total Transactions
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                            {formatCount(byDateQualifyingSummary.transactionCount)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mt: 1 }}>
                            Qualifying transactions in range.
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card
                        sx={{
                          ...chartCardSx,
                          cursor: byDateQualifyingSummary.activityCount > 0 ? 'pointer' : 'default',
                          transition: 'border-color 150ms',
                          '&:hover': byDateQualifyingSummary.activityCount > 0
                            ? { borderColor: 'var(--color-accent)' }
                            : undefined,
                        }}
                        onClick={() => {
                          if (byDateQualifyingSummary.activityCount > 0) {
                            setByDateAllActivityScope('all');
                            setByDateAllActivityDialogOpen(true);
                          }
                        }}
                      >
                        <CardContent sx={{ p: '0 !important' }}>
                          <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                            Total Activities
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                            {formatCount(byDateQualifyingSummary.activityCount)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mt: 1 }}>
                            Picker activity tied to qualifying tx.
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card
                        sx={{
                          ...chartCardSx,
                          cursor: byDateQualifyingSummary.agentCount > 0 ? 'pointer' : 'default',
                          transition: 'border-color 150ms',
                          '&:hover': byDateQualifyingSummary.agentCount > 0
                            ? { borderColor: 'var(--color-accent)' }
                            : undefined,
                        }}
                        onClick={() => {
                          if (byDateQualifyingSummary.agentCount > 0) {
                            setByDateAllAgentsScope('all');
                            setByDateAllAgentsDialogOpen(true);
                          }
                        }}
                      >
                        <CardContent sx={{ p: '0 !important' }}>
                          <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                            Total Agents
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                            {formatCount(byDateQualifyingSummary.agentCount)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mt: 1 }}>
                            Agents with qualifying tx.
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => setByDateConvertedSectionOpen((open) => !open)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setByDateConvertedSectionOpen((open) => !open);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      cursor: 'pointer',
                      userSelect: 'none',
                      width: 'fit-content',
                      '&:focus-visible': { outline: '2px solid var(--color-accent)', outlineOffset: 2 },
                    }}
                    aria-expanded={byDateConvertedSectionOpen}
                  >
                    {byDateConvertedSectionOpen ? (
                      <ExpandLessIcon sx={{ fontSize: 20, color: 'rgba(var(--color-text-rgb), 0.75)' }} />
                    ) : (
                      <ExpandMoreIcon sx={{ fontSize: 20, color: 'rgba(var(--color-text-rgb), 0.75)' }} />
                    )}
                    <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.75)', fontWeight: 700, letterSpacing: 0.6 }}>
                      Converted
                    </Typography>
                  </Box>
                  <Collapse in={byDateConvertedSectionOpen}>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card
                          sx={{
                            ...chartCardSx,
                            cursor: byDateConvertedSummary.transactedAmount > 0 ? 'pointer' : 'default',
                            transition: 'border-color 150ms',
                            '&:hover': byDateConvertedSummary.transactedAmount > 0
                              ? { borderColor: 'var(--color-accent)' }
                              : undefined,
                          }}
                          onClick={() => {
                            if (byDateConvertedSummary.transactedAmount > 0) {
                              void handleByDateAllQualifyingTxClick('amount', 'converted');
                            }
                          }}
                        >
                          <CardContent sx={{ p: '0 !important' }}>
                            <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                              Transacted Amount
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                              {byDateConvertedSummary.transactedAmount.toLocaleString('en-SG', {
                                style: 'currency',
                                currency: 'SGD',
                                maximumFractionDigits: 0,
                              })}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mt: 1 }}>
                              Sum of converted qualifying tx.
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card
                          sx={{
                            ...chartCardSx,
                            cursor: byDateConvertedSummary.transactionCount > 0 ? 'pointer' : 'default',
                            transition: 'border-color 150ms',
                            '&:hover': byDateConvertedSummary.transactionCount > 0
                              ? { borderColor: 'var(--color-accent)' }
                              : undefined,
                          }}
                          onClick={() => {
                            if (byDateConvertedSummary.transactionCount > 0) {
                              void handleByDateAllQualifyingTxClick('count', 'converted');
                            }
                          }}
                        >
                          <CardContent sx={{ p: '0 !important' }}>
                            <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                              Total Transactions
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                              {formatCount(byDateConvertedSummary.transactionCount)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mt: 1 }}>
                              Agent had no tx in the prior 1 year.
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card
                          sx={{
                            ...chartCardSx,
                            cursor: byDateConvertedSummary.activityCount > 0 ? 'pointer' : 'default',
                            transition: 'border-color 150ms',
                            '&:hover': byDateConvertedSummary.activityCount > 0
                              ? { borderColor: 'var(--color-accent)' }
                              : undefined,
                          }}
                          onClick={() => {
                            if (byDateConvertedSummary.activityCount > 0) {
                              setByDateAllActivityScope('converted');
                              setByDateAllActivityDialogOpen(true);
                            }
                          }}
                        >
                          <CardContent sx={{ p: '0 !important' }}>
                            <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                              Total Activities
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                              {formatCount(byDateConvertedSummary.activityCount)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mt: 1 }}>
                              Picker activity tied to converted tx.
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card
                          sx={{
                            ...chartCardSx,
                            cursor: byDateConvertedSummary.agentCount > 0 ? 'pointer' : 'default',
                            transition: 'border-color 150ms',
                            '&:hover': byDateConvertedSummary.agentCount > 0
                              ? { borderColor: 'var(--color-accent)' }
                              : undefined,
                          }}
                          onClick={() => {
                            if (byDateConvertedSummary.agentCount > 0) {
                              setByDateAllAgentsScope('converted');
                              setByDateAllAgentsDialogOpen(true);
                            }
                          }}
                        >
                          <CardContent sx={{ p: '0 !important' }}>
                            <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                              Total Agents
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                              {formatCount(byDateConvertedSummary.agentCount)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mt: 1 }}>
                              Agents with a converted tx.
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Collapse>

                  <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.68)' }}>
                    Each agent is placed in exactly one table based on picker activity and tx count in the selected date range.
                  </Typography>

                  <TextField
                    size="small"
                    placeholder="Search by name, CEA, mobile, or email…"
                    value={byDateSearch}
                    onChange={(e) => setByDateSearch(e.target.value)}
                    sx={{ width: 360, maxWidth: '100%' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18, color: 'rgba(var(--color-text-rgb), 0.45)' }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Tabs
                    value={byDateResultTab}
                    onChange={(_, value) => setByDateResultTab(value as ByDateResultTab)}
                    variant="scrollable"
                    allowScrollButtonsMobile
                    sx={{
                      borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)',
                      '& .MuiTab-root': { color: 'rgba(var(--color-text-rgb), 0.68)', minHeight: 42 },
                      '& .Mui-selected': { color: 'var(--color-accent) !important' },
                      '& .MuiTabs-indicator': { backgroundColor: 'var(--color-accent)' },
                    }}
                  >
                    {byDateResultTabs.map((tab) => (
                      <Tab
                        key={tab.value}
                        value={tab.value}
                        label={`${tab.label} (${formatCount(byDateFilteredBuckets[tab.value].length)})`}
                      />
                    ))}
                  </Tabs>

                  <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {([
                            { field: 'name' as const, label: 'Name', align: 'left' as const },
                            { field: 'ceaNumber' as const, label: 'Cea', align: 'left' as const },
                            { field: 'mobile' as const, label: 'Mobile', align: 'left' as const },
                            { field: 'email' as const, label: 'Email', align: 'left' as const },
                            { field: 'activityCount' as const, label: 'Picker Activity', align: 'right' as const },
                            { field: 'transactionCount' as const, label: 'Tx', align: 'right' as const },
                            { field: 'qualifyingTransactionCount' as const, label: 'Qualifying Tx', align: 'right' as const },
                            { field: 'qualifyingActivityCount' as const, label: 'Qualifying Activity', align: 'right' as const },
                          ] as const).map((col) => (
                            <TableCell
                              key={col.field}
                              align={col.align}
                              sx={{
                                fontWeight: 600,
                                color: 'rgba(var(--color-text-rgb), 0.85)',
                                cursor: 'pointer',
                                userSelect: 'none',
                                '&:hover': { color: 'var(--color-text-strong)' },
                              }}
                              onClick={() => handleByDateSort(col.field)}
                            >
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                {col.label}
                                {byDateSortField === col.field && (
                                  byDateSortDir === 'asc'
                                    ? <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                                    : <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                                )}
                              </Box>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedFilteredActiveByDateRows.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                              {byDateSearch.trim()
                                ? 'No agents matching your search in this category.'
                                : 'No agents in this category for the selected date range.'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          sortedFilteredActiveByDateRows.map((row) => (
                            <TableRow key={row.ceaNumber}>
                              <TableCell>{row.name?.trim() ? row.name : '—'}</TableCell>
                              <TableCell>{row.ceaNumber}</TableCell>
                              <TableCell>
                          {row.mobile?.trim() ? (
                            <a
                              href={`tel:${row.mobile}`}
                              style={{ color: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                            >
                              {row.mobile}
                            </a>
                          ) : '—'}
                        </TableCell>
                              <TableCell>{row.email?.trim() ? row.email : '—'}</TableCell>
                              <TableCell align="right">
                                {row.activityCount > 0 ? (
                                  <Button
                                    size="small"
                                    variant="text"
                                    sx={{ minWidth: 'auto', p: 0, textTransform: 'none', fontWeight: 600, color: '#38B2AC' }}
                                    onClick={() => void handleByDateActivityClick(row)}
                                  >
                                    {formatCount(row.activityCount)}
                                  </Button>
                                ) : (
                                  <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.45)' }}>0</Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {row.transactionCount > 0 ? (
                                  <Button
                                    size="small"
                                    variant="text"
                                    sx={{ minWidth: 'auto', p: 0, textTransform: 'none', fontWeight: 600, color: '#4299E1' }}
                                    onClick={() => void handleByDateTransactionClick(row)}
                                  >
                                    {formatCount(row.transactionCount)}
                                  </Button>
                                ) : (
                                  <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.45)' }}>0</Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {row.qualifyingTransactionCount > 0 ? (
                                  <Button
                                    size="small"
                                    variant="text"
                                    sx={{ minWidth: 'auto', p: 0, textTransform: 'none', fontWeight: 600, color: '#A78BFA' }}
                                    onClick={() => void handleByDateQualifyingTxClick(row)}
                                  >
                                    {formatCount(row.qualifyingTransactionCount)}
                                  </Button>
                                ) : (
                                  <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.45)' }}>0</Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {row.qualifyingActivityCount > 0 ? (
                                  <Button
                                    size="small"
                                    variant="text"
                                    sx={{ minWidth: 'auto', p: 0, textTransform: 'none', fontWeight: 600, color: '#F59E0B' }}
                                    onClick={() => void handleByDateQualifyingActivityClick(row)}
                                  >
                                    {formatCount(row.qualifyingActivityCount)}
                                  </Button>
                                ) : (
                                  <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.45)' }}>0</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              )}
            </>
          ) : activeTab === 'successCloser' ? (
            <>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <DateRangeSelector
                  label="Date Range"
                  range={successCloserRange}
                  onApply={setSuccessCloserRange}
                />
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={() => void fetchSuccessCloser()}
                  sx={{ bgcolor: 'var(--color-accent)', '&:hover': { bgcolor: 'var(--color-accent-strong)' } }}
                >
                  Search
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={successCloserShowTagger}
                      onChange={(e) => setSuccessCloserShowTagger(e.target.checked)}
                      sx={{ color: 'rgba(var(--color-text-rgb), 0.5)', '&.Mui-checked': { color: 'var(--color-accent)' } }}
                    />
                  }
                  label={<Typography variant="body2" sx={{ color: 'var(--color-text-strong)' }}>Tagger</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={successCloserShowMainEntity}
                      onChange={(e) => setSuccessCloserShowMainEntity(e.target.checked)}
                      sx={{ color: 'rgba(var(--color-text-rgb), 0.5)', '&.Mui-checked': { color: 'var(--color-accent)' } }}
                    />
                  }
                  label={<Typography variant="body2" sx={{ color: 'var(--color-text-strong)' }}>Hyper</Typography>}
                />
              </Box>

              {successCloserError ? <Alert severity="error">{successCloserError}</Alert> : null}

              {successCloserLoading ? (
                <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : successCloserRows.length === 0 ? (
                <Box
                  sx={{
                    py: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 1.5,
                    border: '1px dashed rgba(var(--color-text-rgb), 0.14)',
                    bgcolor: 'rgba(var(--color-text-rgb), 0.02)',
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                    Select a date range and click Search to view success closer data.
                  </Typography>
                </Box>
              ) : (
                <>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Filtered Agents ({filteredSuccessCloserRows.length})
                </Typography>
                <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Agent Name</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>CEA Reg No</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Mobile</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Activities</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Closings</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredSuccessCloserRows.map((row) => (
                        <TableRow key={row.ceaRegNo}>
                          <TableCell>{row.agentName}</TableCell>
                          <TableCell>{row.ceaRegNo}</TableCell>
                          <TableCell>{row.mobile}</TableCell>
                          <TableCell align="right">{formatCount(row.activityCount)}</TableCell>
                          <TableCell align="right">
                            <Typography
                              component="span"
                              sx={{
                                color: 'var(--color-accent)',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                '&:hover': { opacity: 0.8 },
                              }}
                              onClick={() => setSuccessCloserTxDialog(row)}
                            >
                              {formatCount(row.closingCount)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                </>
              )}
            </>
          ) : activeTab === 'jyUsers' ? (
            <>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <DateRangeSelector
                  label="Picker Usage Range"
                  range={jyUsersRange}
                  onApply={setJyUsersRange}
                />
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => void fetchJyUsers()}
                  sx={{ borderColor: 'rgba(var(--color-text-rgb), 0.18)', color: 'rgba(var(--color-text-rgb), 0.88)' }}
                >
                  Refresh
                </Button>
              </Box>

              <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.68)' }}>
                Active R-number agents with &gt; S$50K total commission GCE in the last 12 months where resale makes up at least 85% of that income (Resale Agent / JY rule). Picker usage reflects picker activity in the selected date range.
              </Typography>

              {jyUsersError ? <Alert severity="error">{jyUsersError}</Alert> : null}

              {jyUsersLoading ? (
                <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : !jyUsersHasSearched ? null : jyUsersRows.length === 0 ? (
                <Alert severity="info">No JY users matched the criteria.</Alert>
              ) : (
                <Stack spacing={2.5}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Card sx={chartCardSx}>
                        <CardContent sx={{ p: '0 !important' }}>
                          <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                            Total JY Users
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                            {formatCount(jyUsersSummary.total)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mt: 1 }}>
                            Resale-focused agents matching the JY rule.
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Card sx={chartCardSx}>
                        <CardContent sx={{ p: '0 !important' }}>
                          <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                            Used Picker
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, color: '#38B2AC' }}>
                            {formatCount(jyUsersSummary.usedPicker)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mt: 1 }}>
                            {jyUsersSummary.total > 0
                              ? `${Math.round((jyUsersSummary.usedPicker / jyUsersSummary.total) * 100)}% of JY users`
                              : 'No data'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Card sx={chartCardSx}>
                        <CardContent sx={{ p: '0 !important' }}>
                          <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                            No Usage
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                            {formatCount(jyUsersSummary.notUsedPicker)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', mt: 1 }}>
                            {jyUsersSummary.total > 0
                              ? `${Math.round((jyUsersSummary.notUsedPicker / jyUsersSummary.total) * 100)}% of JY users`
                              : 'No data'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  <Box sx={chartCardSx}>
                    <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                      Picker Usage
                    </Typography>
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie
                          data={jyUsersPieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          label={(entry: any) => `${entry.name}: ${entry.value}`}
                        >
                          {jyUsersPieData.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip
                          formatter={(value: any, name: any) => [formatCount(Number(value)), name]}
                          contentStyle={{ ...analyticsBarTooltipSx, background: 'rgba(20, 28, 43, 0.96)' }}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="Search by name, CEA, mobile, or email…"
                      value={jyUsersSearch}
                      onChange={(e) => setJyUsersSearch(e.target.value)}
                      sx={{ width: 360, maxWidth: '100%' }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 18, color: 'rgba(var(--color-text-rgb), 0.45)' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Tabs
                    value={jyUsersUsageFilter}
                    onChange={(_, value) => setJyUsersUsageFilter(value as 'all' | 'used' | 'notUsed')}
                    sx={{
                      borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)',
                      '& .MuiTab-root': { color: 'rgba(var(--color-text-rgb), 0.68)', minHeight: 42 },
                      '& .Mui-selected': { color: 'var(--color-accent) !important' },
                      '& .MuiTabs-indicator': { backgroundColor: 'var(--color-accent)' },
                    }}
                  >
                    <Tab value="all" label={`All (${jyUsersSummary.total})`} />
                    <Tab value="used" label={`Used Picker (${jyUsersSummary.usedPicker})`} />
                    <Tab value="notUsed" label={`No Usage (${jyUsersSummary.notUsedPicker})`} />
                  </Tabs>

                  <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {([
                            { field: 'ceaNumber' as const, label: 'CEA', align: 'left' as const },
                            { field: 'name' as const, label: 'Name', align: 'left' as const },
                            { field: 'mobile' as const, label: 'Mobile', align: 'left' as const },
                            { field: 'resalePercent' as const, label: 'Resale %', align: 'right' as const },
                            { field: 'activityCount' as const, label: 'Picker Usage', align: 'right' as const },
                          ] as const).map((col) => (
                            <TableCell
                              key={col.field}
                              align={col.align}
                              sx={{
                                fontWeight: 600,
                                color: 'rgba(var(--color-text-rgb), 0.85)',
                                cursor: 'pointer',
                                userSelect: 'none',
                                whiteSpace: 'nowrap',
                                '&:hover': { color: 'var(--color-text-strong)' },
                              }}
                              onClick={() => handleJyUsersSort(col.field)}
                            >
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                {col.label}
                                {jyUsersSortField === col.field && (
                                  jyUsersSortDir === 'asc'
                                    ? <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                                    : <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                                )}
                              </Box>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredSortedJyUsers.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                              {jyUsersSearch.trim()
                                ? 'No JY users matching your search.'
                                : 'No JY users in this category.'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredSortedJyUsers.map((row) => (
                            <TableRow key={row.ceaNumber}>
                              <TableCell>{row.ceaNumber}</TableCell>
                              <TableCell>{row.name?.trim() ? row.name : '—'}</TableCell>
                              <TableCell>
                                {row.mobile?.trim() ? (
                                  <a
                                    href={`tel:${row.mobile}`}
                                    style={{ color: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                                  >
                                    {row.mobile}
                                  </a>
                                ) : '—'}
                              </TableCell>
                              <TableCell align="right">
                                {Math.round(row.resalePercent * 100)}%
                              </TableCell>
                              <TableCell align="right">
                                {row.usedPicker ? (
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, justifyContent: 'flex-end' }}>
                                    <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#38B2AC' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#38B2AC' }}>
                                      Used ({formatCount(row.activityCount)})
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.45)' }}>
                                    No usage
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              )}
            </>
          ) : activeTab === 'checkUser' ? (
            <CheckUserTab />
          ) : activeTab === 'shareLinks' ? (
            <ShareLinksAnalyticsTab />
          ) : activeTab === 'tokenUsage' ? (
            // Token Usage tab — embed the existing TokenUsageAnalyticsDialog
            // in admin mode so admins reuse the same overview / drill-down
            // surface as agents but with cross-agent rollups.
            <TokenUsageAnalyticsDialog
              open
              onClose={onClose}
              mode="admin"
              embedded
            />
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Button onClick={onClose} sx={{ color: 'rgba(var(--color-text-rgb), 0.9)' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={!!byDateActivityDialog}
      onClose={() => setByDateActivityDialog(null)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              Picker Activity Details
              {byDateActivityDialog ? ` - ${byDateActivityDialog.name} (${byDateActivityDialog.ceaNumber})` : ''}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
              Range: {formatDisplayRange(byDateRange)}
            </Typography>
          </Box>
          <IconButton onClick={() => setByDateActivityDialog(null)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        {byDateActivityLoading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : byDateActivityError ? (
          <Alert severity="error">{byDateActivityError}</Alert>
        ) : !byDateActivityUsage ? (
          <Typography sx={{ py: 2, color: 'rgba(var(--color-text-rgb), 0.65)' }}>
            No picker activity details found for this period.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Event Breakdown ({formatCount(byDateActivityUsage.totalEvents)} total)
              </Typography>
              <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Event</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byDateActivityEventUsage.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                          No events found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      byDateActivityEventUsage.map((event) => (
                        <TableRow key={event.eventName}>
                          <TableCell>{event.eventName}</TableCell>
                          <TableCell align="right">{formatCount(event.count)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Project Views
              </Typography>
              <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Project</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Views</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byDateActivityProjectViews.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                          No project views found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      byDateActivityProjectViews.map((project) => (
                        <TableRow key={project.projectName}>
                          <TableCell>{project.projectName}</TableCell>
                          <TableCell align="right">{formatCount(project.views)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>

    <Dialog
      open={!!byDateTxDialog}
      onClose={() => setByDateTxDialog(null)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              Transaction Details
              {byDateTxDialog ? ` - ${byDateTxDialog.name} (${byDateTxDialog.ceaNumber})` : ''}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
              Range: {formatDisplayRange(byDateRange)}
            </Typography>
          </Box>
          <IconButton onClick={() => setByDateTxDialog(null)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        {byDateTxLoading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : byDateTxError ? (
          <Alert severity="error">{byDateTxError}</Alert>
        ) : byDateTxUnits.length === 0 ? (
          <Typography sx={{ py: 2, color: 'rgba(var(--color-text-rgb), 0.65)' }}>
            No transactions found in this period.
          </Typography>
        ) : (
          <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Transaction No</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Project</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Unit No</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Option Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Price</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Agent role</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byDateTxUnits.map((unit, index) => (
                  <TableRow key={`${unit.transactionNo}-${unit.unitNo}-${unit.optionDate}-${index}`}>
                    <TableCell>{unit.transactionNo}</TableCell>
                    <TableCell>{unit.reference}</TableCell>
                    <TableCell>{unit.unitNo}</TableCell>
                    <TableCell>{unit.optionDate}</TableCell>
                    <TableCell align="right">
                      {unit.transactionPrice.toLocaleString('en-SG', { style: 'currency', currency: 'SGD', minimumFractionDigits: 0 })}
                    </TableCell>
                    <TableCell>{formatAgentRoleLabel(unit.agentRole)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>

    <Dialog
      open={!!byDateQualifyingTxDialog}
      onClose={() => setByDateQualifyingTxDialog(null)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              Qualifying Transactions
              {byDateQualifyingTxDialog ? ` - ${byDateQualifyingTxDialog.name} (${byDateQualifyingTxDialog.ceaNumber})` : ''}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
              Range: {formatDisplayRange(byDateRange)}
            </Typography>
          </Box>
          <IconButton onClick={() => setByDateQualifyingTxDialog(null)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        {byDateQualifyingTxLoading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : byDateQualifyingTxError ? (
          <Alert severity="error">{byDateQualifyingTxError}</Alert>
        ) : byDateQualifyingTxList.length === 0 ? (
          <Typography sx={{ py: 2, color: 'rgba(var(--color-text-rgb), 0.65)' }}>
            No qualifying transactions found in this period.
          </Typography>
        ) : (
          <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Transaction No</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Project</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Unit No</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Option Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Price</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Agent role</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byDateQualifyingTxList.map((tx, index) => (
                  <TableRow key={`${tx.transactionNo}-${tx.unitNo}-${tx.optionDate}-${index}`}>
                    <TableCell>{tx.transactionNo}</TableCell>
                    <TableCell>{tx.projectName}</TableCell>
                    <TableCell>{tx.unitNo}</TableCell>
                    <TableCell>{tx.optionDate}</TableCell>
                    <TableCell align="right">
                      {tx.transactionPrice.toLocaleString('en-SG', { style: 'currency', currency: 'SGD', minimumFractionDigits: 0 })}
                    </TableCell>
                    <TableCell>{formatAgentRoleLabel(tx.agentRole)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>

    <Dialog
      open={!!byDateQualifyingActivityDialog}
      onClose={() => setByDateQualifyingActivityDialog(null)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              Qualifying Activity
              {byDateQualifyingActivityDialog ? ` - ${byDateQualifyingActivityDialog.name} (${byDateQualifyingActivityDialog.ceaNumber})` : ''}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
              Range: {formatDisplayRange(byDateRange)} · Pre-option activity per project
            </Typography>
          </Box>
          <IconButton onClick={() => setByDateQualifyingActivityDialog(null)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        {byDateQualifyingActivityLoading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : byDateQualifyingActivityError ? (
          <Alert severity="error">{byDateQualifyingActivityError}</Alert>
        ) : byDateQualifyingActivityList.length === 0 ? (
          <Typography sx={{ py: 2, color: 'rgba(var(--color-text-rgb), 0.65)' }}>
            No qualifying activity found in this period.
          </Typography>
        ) : (
          <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Project</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Latest Option Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Pre-option Activity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byDateQualifyingActivityList.map((item) => (
                  <TableRow key={item.projectName}>
                    <TableCell>{item.projectName}</TableCell>
                    <TableCell>{item.latestOptionDay}</TableCell>
                    <TableCell align="right">{formatCount(item.activityCount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>

    <Dialog
      open={!!byDateAllTxDialog}
      onClose={() => setByDateAllTxDialog(null)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              {byDateAllTxScope === 'converted'
                ? (byDateAllTxDialog === 'amount' ? 'Converted Transacted Amount' : 'Converted Transactions')
                : (byDateAllTxDialog === 'amount' ? 'Transacted Amount Breakdown' : 'Qualifying Transactions')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
              Range: {formatDisplayRange(byDateRange)} ·{' '}
              {byDateAllTxScope === 'converted'
                ? 'Agents with no transaction in the prior 1 year.'
                : 'Unique qualifying transactions across all agents.'}
            </Typography>
          </Box>
          <IconButton onClick={() => setByDateAllTxDialog(null)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        {byDateAllTxLoading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : byDateAllTxError ? (
          <Alert severity="error">{byDateAllTxError}</Alert>
        ) : byDateAllTxListFiltered.length === 0 ? (
          <Typography sx={{ py: 2, color: 'rgba(var(--color-text-rgb), 0.65)' }}>
            No qualifying transactions found in this period.
          </Typography>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.75)' }}>
              {formatCount(byDateAllTxListFiltered.length)} transactions ·{' '}
              {byDateAllTxListFiltered
                .reduce((s, t) => s + (t.transactionPrice || 0), 0)
                .toLocaleString('en-SG', { style: 'currency', currency: 'SGD', maximumFractionDigits: 0 })}
            </Typography>
            <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }} sortDirection={byDateAllTxSort.field === 'transactionNo' ? byDateAllTxSort.dir : false}>
                      <TableSortLabel active={byDateAllTxSort.field === 'transactionNo'} direction={byDateAllTxSort.field === 'transactionNo' ? byDateAllTxSort.dir : 'asc'} onClick={() => toggleByDateAllTxSort('transactionNo')}>Transaction No</TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }} sortDirection={byDateAllTxSort.field === 'projectName' ? byDateAllTxSort.dir : false}>
                      <TableSortLabel active={byDateAllTxSort.field === 'projectName'} direction={byDateAllTxSort.field === 'projectName' ? byDateAllTxSort.dir : 'asc'} onClick={() => toggleByDateAllTxSort('projectName')}>Project</TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }} sortDirection={byDateAllTxSort.field === 'unitNo' ? byDateAllTxSort.dir : false}>
                      <TableSortLabel active={byDateAllTxSort.field === 'unitNo'} direction={byDateAllTxSort.field === 'unitNo' ? byDateAllTxSort.dir : 'asc'} onClick={() => toggleByDateAllTxSort('unitNo')}>Unit No</TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }} sortDirection={byDateAllTxSort.field === 'optionDate' ? byDateAllTxSort.dir : false}>
                      <TableSortLabel active={byDateAllTxSort.field === 'optionDate'} direction={byDateAllTxSort.field === 'optionDate' ? byDateAllTxSort.dir : 'asc'} onClick={() => toggleByDateAllTxSort('optionDate')}>Option Date</TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }} sortDirection={byDateAllTxSort.field === 'transactionPrice' ? byDateAllTxSort.dir : false}>
                      <TableSortLabel active={byDateAllTxSort.field === 'transactionPrice'} direction={byDateAllTxSort.field === 'transactionPrice' ? byDateAllTxSort.dir : 'asc'} onClick={() => toggleByDateAllTxSort('transactionPrice')}>Price</TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {byDateAllTxListFiltered.map((tx, index) => (
                    <TableRow key={`${tx.transactionNo}-${tx.unitNo}-${tx.optionDate}-${index}`}>
                      <TableCell>{tx.transactionNo}</TableCell>
                      <TableCell>{tx.projectName}</TableCell>
                      <TableCell>{tx.unitNo}</TableCell>
                      <TableCell>{tx.optionDate}</TableCell>
                      <TableCell align="right">
                        {tx.transactionPrice.toLocaleString('en-SG', { style: 'currency', currency: 'SGD', minimumFractionDigits: 0 })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}
      </DialogContent>
    </Dialog>

    <Dialog
      open={byDateAllActivityDialogOpen}
      onClose={() => setByDateAllActivityDialogOpen(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              {byDateAllActivityScope === 'converted' ? 'Converted Activities by Agent' : 'Total Qualifying Activities by Agent'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
              Range: {formatDisplayRange(byDateRange)}
            </Typography>
          </Box>
          <IconButton onClick={() => setByDateAllActivityDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }} sortDirection={byDateActivityDialogSort.field === 'name' ? byDateActivityDialogSort.dir : false}>
                  <TableSortLabel active={byDateActivityDialogSort.field === 'name'} direction={byDateActivityDialogSort.field === 'name' ? byDateActivityDialogSort.dir : 'asc'} onClick={() => toggleByDateActivityDialogSort('name')}>Agent</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }} sortDirection={byDateActivityDialogSort.field === 'ceaNumber' ? byDateActivityDialogSort.dir : false}>
                  <TableSortLabel active={byDateActivityDialogSort.field === 'ceaNumber'} direction={byDateActivityDialogSort.field === 'ceaNumber' ? byDateActivityDialogSort.dir : 'asc'} onClick={() => toggleByDateActivityDialogSort('ceaNumber')}>CEA</TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }} sortDirection={byDateActivityDialogSort.field === 'tx' ? byDateActivityDialogSort.dir : false}>
                  <TableSortLabel active={byDateActivityDialogSort.field === 'tx'} direction={byDateActivityDialogSort.field === 'tx' ? byDateActivityDialogSort.dir : 'asc'} onClick={() => toggleByDateActivityDialogSort('tx')}>
                    {byDateAllActivityScope === 'converted' ? 'Converted Tx' : 'Qualifying Tx'}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }} sortDirection={byDateActivityDialogSort.field === 'activity' ? byDateActivityDialogSort.dir : false}>
                  <TableSortLabel active={byDateActivityDialogSort.field === 'activity'} direction={byDateActivityDialogSort.field === 'activity' ? byDateActivityDialogSort.dir : 'asc'} onClick={() => toggleByDateActivityDialogSort('activity')}>
                    {byDateAllActivityScope === 'converted' ? 'Converted Activity' : 'Qualifying Activity'}
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {byDateActivityDialogRows.map((r) => (
                <TableRow key={r.ceaNumber}>
                  <TableCell>{r.name?.trim() || '—'}</TableCell>
                  <TableCell>{r.ceaNumber}</TableCell>
                  <TableCell align="right">
                    {formatCount(byDateAllActivityScope === 'converted' ? r.convertedTransactionCount : r.qualifyingTransactionCount)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCount(byDateAllActivityScope === 'converted' ? r.convertedActivityCount : r.qualifyingActivityCount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>

    <Dialog
      open={byDateAllAgentsDialogOpen}
      onClose={() => setByDateAllAgentsDialogOpen(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              {byDateAllAgentsScope === 'converted' ? 'Agents with Converted Transactions' : 'Agents with Qualifying Transactions'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
              Range: {formatDisplayRange(byDateRange)}
            </Typography>
          </Box>
          <IconButton onClick={() => setByDateAllAgentsDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }} sortDirection={byDateAgentsDialogSort.field === 'name' ? byDateAgentsDialogSort.dir : false}>
                  <TableSortLabel active={byDateAgentsDialogSort.field === 'name'} direction={byDateAgentsDialogSort.field === 'name' ? byDateAgentsDialogSort.dir : 'asc'} onClick={() => toggleByDateAgentsDialogSort('name')}>Name</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }} sortDirection={byDateAgentsDialogSort.field === 'ceaNumber' ? byDateAgentsDialogSort.dir : false}>
                  <TableSortLabel active={byDateAgentsDialogSort.field === 'ceaNumber'} direction={byDateAgentsDialogSort.field === 'ceaNumber' ? byDateAgentsDialogSort.dir : 'asc'} onClick={() => toggleByDateAgentsDialogSort('ceaNumber')}>CEA</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }} sortDirection={byDateAgentsDialogSort.field === 'mobile' ? byDateAgentsDialogSort.dir : false}>
                  <TableSortLabel active={byDateAgentsDialogSort.field === 'mobile'} direction={byDateAgentsDialogSort.field === 'mobile' ? byDateAgentsDialogSort.dir : 'asc'} onClick={() => toggleByDateAgentsDialogSort('mobile')}>Mobile</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }} sortDirection={byDateAgentsDialogSort.field === 'email' ? byDateAgentsDialogSort.dir : false}>
                  <TableSortLabel active={byDateAgentsDialogSort.field === 'email'} direction={byDateAgentsDialogSort.field === 'email' ? byDateAgentsDialogSort.dir : 'asc'} onClick={() => toggleByDateAgentsDialogSort('email')}>Email</TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }} sortDirection={byDateAgentsDialogSort.field === 'tx' ? byDateAgentsDialogSort.dir : false}>
                  <TableSortLabel active={byDateAgentsDialogSort.field === 'tx'} direction={byDateAgentsDialogSort.field === 'tx' ? byDateAgentsDialogSort.dir : 'asc'} onClick={() => toggleByDateAgentsDialogSort('tx')}>
                    {byDateAllAgentsScope === 'converted' ? 'Converted Tx' : 'Qualifying Tx'}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }} sortDirection={byDateAgentsDialogSort.field === 'activity' ? byDateAgentsDialogSort.dir : false}>
                  <TableSortLabel active={byDateAgentsDialogSort.field === 'activity'} direction={byDateAgentsDialogSort.field === 'activity' ? byDateAgentsDialogSort.dir : 'asc'} onClick={() => toggleByDateAgentsDialogSort('activity')}>
                    {byDateAllAgentsScope === 'converted' ? 'Converted Activity' : 'Qualifying Activity'}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }} sortDirection={byDateAgentsDialogSort.field === 'amount' ? byDateAgentsDialogSort.dir : false}>
                  <TableSortLabel active={byDateAgentsDialogSort.field === 'amount'} direction={byDateAgentsDialogSort.field === 'amount' ? byDateAgentsDialogSort.dir : 'asc'} onClick={() => toggleByDateAgentsDialogSort('amount')}>Transacted Amount</TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {byDateAgentsDialogRows.map((r) => (
                <TableRow key={r.ceaNumber}>
                  <TableCell>{r.name?.trim() || '—'}</TableCell>
                  <TableCell>{r.ceaNumber}</TableCell>
                  <TableCell>{r.mobile?.trim() || '—'}</TableCell>
                  <TableCell>{r.email?.trim() || '—'}</TableCell>
                  <TableCell align="right">
                    {formatCount(byDateAllAgentsScope === 'converted' ? r.convertedTransactionCount : r.qualifyingTransactionCount)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCount(byDateAllAgentsScope === 'converted' ? r.convertedActivityCount : r.qualifyingActivityCount)}
                  </TableCell>
                  <TableCell align="right">
                    {((byDateAllAgentsScope === 'converted' ? r.convertedTransactionAmount : r.qualifyingTransactionAmount) || 0).toLocaleString('en-SG', {
                      style: 'currency',
                      currency: 'SGD',
                      maximumFractionDigits: 0,
                    })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>

    <Dialog
      open={!!drillDownProject}
      onClose={handleCloseDrillDown}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          <Box>
            <Typography variant="h6">
              {drillDownProject ? drillDownProject.projectName : 'Project'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.56)', display: 'block', mt: 0.5 }}>
              {`Views (selected period) use the Overview date range: ${formatDisplayRange(overviewRange)}`}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDrillDown} size="small" sx={{ mt: -0.5 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={2}>
          <Tabs
            value={drillDownTab}
            onChange={(_e, val) => setDrillDownTab(val as DrillDownTab)}
            sx={{
              borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)',
              '& .MuiTab-root': { textTransform: 'none', color: 'rgba(var(--color-text-rgb), 0.65)' },
              '& .Mui-selected': { color: 'var(--color-text-strong) !important' },
            }}
          >
            <Tab value="all" label={`All Users (${filteredSortedDrillDownUsers.length})`} />
            <Tab value="eoi" label={`EOI (${filteredSortedDrillDownEois.length})`} />
          </Tabs>

          <Stack spacing={1}>
            <FormControlLabel
              control={(
                <Checkbox
                  size="small"
                  checked={drillDownRemoveConvertedAgents}
                  onChange={(e) => setDrillDownRemoveConvertedAgents(e.target.checked)}
                />
              )}
              label="Remove Converted Agent"
            />
            <TextField
              size="small"
              placeholder="Filter by CEA, name, or mobile…"
              value={drillDownFilter}
              onChange={(e) => setDrillDownFilter(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'rgba(var(--color-text-rgb), 0.45)', fontSize: 20 }} />,
              }}
              fullWidth
              sx={{ '& .MuiInputBase-input': { color: 'var(--color-text-strong)' } }}
            />
          </Stack>

          {drillDownTab === 'eoi' && drillDownEoiSendError && (
            <Alert severity="error" onClose={() => setDrillDownEoiSendError('')}>
              {drillDownEoiSendError}
            </Alert>
          )}

          {drillDownTab === 'all' ? (drillDownLoading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
              <Table
                size="small"
                sx={{
                  width: '100%',
                  tableLayout: 'fixed',
                  '& th, & td': { px: 1, wordBreak: 'break-word' },
                  '& th': { whiteSpace: 'normal !important' },
                }}
              >
                <TableHead>
                  <TableRow>
                    {([
                      { field: 'ceaNumber' as const, label: 'CEA Number' },
                      { field: 'name' as const, label: 'Name' },
                      { field: 'mobile' as const, label: 'Mobile' },
                    ] as const).map((col) => (
                      <TableCell
                        key={col.field}
                        sx={{
                          fontWeight: 600,
                          color: 'rgba(var(--color-text-rgb), 0.85)',
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { color: 'var(--color-text-strong)' },
                        }}
                        onClick={() => handleDrillDownSort(col.field)}
                      >
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          {col.label}
                          {drillDownSortField === col.field && (
                            drillDownSortDir === 'asc'
                              ? <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                              : <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          )}
                        </Box>
                      </TableCell>
                    ))}
                    <AnalyticsMetricTableHeadCell
                      field="viewsAllTime"
                      label="Views (all time)"
                      hideZero={drillDownHideZeroViewsAllTime}
                      onHideZeroChange={setDrillDownHideZeroViewsAllTime}
                      sortField={drillDownSortField}
                      sortDir={drillDownSortDir}
                      onSort={handleDrillDownSort}
                    />
                    <AnalyticsMetricTableHeadCell
                      field="viewsPeriod"
                      label="Views (selected period)"
                      hideZero={drillDownHideZeroViewsPeriod}
                      onHideZeroChange={setDrillDownHideZeroViewsPeriod}
                      sortField={drillDownSortField}
                      sortDir={drillDownSortDir}
                      onSort={handleDrillDownSort}
                    />
                    <AnalyticsMetricTableHeadCell
                      field="soldAllTime"
                      label="Sold (all time)"
                      hideZero={drillDownHideZeroSoldAllTime}
                      onHideZeroChange={setDrillDownHideZeroSoldAllTime}
                      sortField={drillDownSortField}
                      sortDir={drillDownSortDir}
                      onSort={handleDrillDownSort}
                    />
                    <AnalyticsMetricTableHeadCell
                      field="soldPeriod"
                      label="Sold (selected period)"
                      hideZero={drillDownHideZeroSoldPeriod}
                      onHideZeroChange={setDrillDownHideZeroSoldPeriod}
                      sortField={drillDownSortField}
                      sortDir={drillDownSortDir}
                      onSort={handleDrillDownSort}
                    />
                    <CloseNewLaunchTableHeadCell
                      checked={drillDownHideNonZeroCloseNl}
                      onCheckedChange={setDrillDownHideNonZeroCloseNl}
                      sortField={drillDownSortField}
                      sortDir={drillDownSortDir}
                      onSort={() => handleDrillDownSort('closeNewLaunchCount')}
                    />
                    <ResaleAgentTableHeadCell
                      checked={drillDownOnlyResaleAgent}
                      onCheckedChange={setDrillDownOnlyResaleAgent}
                    />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSortedDrillDownUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                        {drillDownFilter || drillDownRemoveConvertedAgents || drillDownHideNonZeroCloseNl || drillDownOnlyResaleAgent
                          ? 'No results matching your filter.'
                          : 'No browsing or transaction data for this project.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSortedDrillDownUsers.map((row) => (
                      <TableRow key={row.ceaNumber}>
                        <TableCell>{row.ceaNumber}</TableCell>
                        <TableCell>{row.name?.trim() ? row.name : '—'}</TableCell>
                        <TableCell>
                          {row.mobile?.trim() ? (
                            <a
                              href={`tel:${row.mobile}`}
                              style={{ color: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                            >
                              {row.mobile}
                            </a>
                          ) : '—'}
                        </TableCell>
                        <TableCell align="right">{formatCount(row.viewsAllTime)}</TableCell>
                        <TableCell align="right">{formatCount(row.viewsPeriod)}</TableCell>
                        <TableCell align="right">
                          {drillDownSoldRateLimited ? (
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(var(--color-text-rgb), 0.65)',
                                display: 'block',
                                textAlign: 'right',
                                maxWidth: 260,
                                ml: 'auto',
                                lineHeight: 1.35,
                              }}
                            >
                              {SOLD_COLUMN_RATE_LIMIT_MESSAGE}
                            </Typography>
                          ) : row.soldAllTime > 0 ? (
                            <Button
                              size="small"
                              variant="text"
                              sx={{ minWidth: 'auto', p: 0, textTransform: 'none', fontWeight: 600, color: '#4299E1' }}
                              onClick={() => handleSoldCountClick(row.ceaNumber, 'allTime', row.name)}
                            >
                              {formatCount(row.soldAllTime)}
                            </Button>
                          ) : (
                            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.45)' }}>
                              0
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {drillDownSoldRateLimited ? (
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(var(--color-text-rgb), 0.65)',
                                display: 'block',
                                textAlign: 'right',
                                maxWidth: 260,
                                ml: 'auto',
                                lineHeight: 1.35,
                              }}
                            >
                              {SOLD_COLUMN_RATE_LIMIT_MESSAGE}
                            </Typography>
                          ) : row.soldPeriod > 0 ? (
                            <Button
                              size="small"
                              variant="text"
                              sx={{ minWidth: 'auto', p: 0, textTransform: 'none', fontWeight: 600, color: '#4299E1' }}
                              onClick={() => handleSoldCountClick(row.ceaNumber, 'selectedPeriod', row.name)}
                            >
                              {formatCount(row.soldPeriod)}
                            </Button>
                          ) : (
                            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.45)' }}>
                              0
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{formatCount(row.closeNewLaunchCount || 0)}</TableCell>
                        <TableCell align="right">
                          {row.isResaleAgent ? (
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#48BB78' }}>Yes</Typography>
                          ) : (
                            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.45)' }}>No</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )) : (drillDownEoisLoading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {([
                      { field: 'ceaNumber' as const, label: 'CEA Number', align: 'left' as const },
                      { field: 'name' as const, label: 'Name', align: 'left' as const },
                      { field: 'mobile' as const, label: 'Mobile', align: 'left' as const },
                      { field: 'viewsPeriod' as const, label: 'Views', align: 'right' as const },
                      { field: 'eoiCount' as const, label: 'No. EOIs', align: 'right' as const },
                    ] as const).map((col) => (
                      <TableCell
                        key={col.field}
                        align={col.align}
                        sx={{
                          fontWeight: 600,
                          color: 'rgba(var(--color-text-rgb), 0.85)',
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { color: 'var(--color-text-strong)' },
                        }}
                        onClick={() => handleDrillDownEoiSort(col.field)}
                      >
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          {col.label}
                          {drillDownEoiSortField === col.field && (
                            drillDownEoiSortDir === 'asc'
                              ? <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                              : <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          )}
                        </Box>
                      </TableCell>
                    ))}
                    <CloseNewLaunchTableHeadCell
                      checked={drillDownHideNonZeroCloseNl}
                      onCheckedChange={setDrillDownHideNonZeroCloseNl}
                      sortField={drillDownEoiSortField}
                      sortDir={drillDownEoiSortDir}
                      onSort={() => handleDrillDownEoiSort('closeNewLaunchCount')}
                    />
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSortedDrillDownEois.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                        {drillDownFilter || drillDownRemoveConvertedAgents || drillDownHideNonZeroCloseNl
                          ? 'No results matching your filter.'
                          : 'No EOIs registered for this project.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSortedDrillDownEois.map((row) => {
                      const sending = !!drillDownEoiSending[row.ceaNumber];
                      const disabled = sending || !row.mobile?.trim();
                      return (
                      <TableRow key={row.ceaNumber}>
                        <TableCell>{row.ceaNumber}</TableCell>
                        <TableCell>{row.name?.trim() ? row.name : '—'}</TableCell>
                        <TableCell>
                          {row.mobile?.trim() ? (
                            <a
                              href={`tel:${row.mobile}`}
                              style={{ color: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                            >
                              {row.mobile}
                            </a>
                          ) : '—'}
                        </TableCell>
                        <TableCell align="right">{formatCount(row.viewsPeriod)}</TableCell>
                        <TableCell align="right">{formatCount(row.eoiCount)}</TableCell>
                        <TableCell align="right">{formatCount(row.closeNewLaunchCount || 0)}</TableCell>
                        <TableCell align="center">
                          <Stack alignItems="center" spacing={0.5}>
                            <Button
                              size="small"
                              variant="contained"
                              disabled={disabled}
                              onClick={() => void handleSendEoiWhatsApp(row.ceaNumber)}
                              startIcon={
                                sending
                                  ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
                                  : <WhatsAppIcon sx={{ fontSize: 16 }} />
                              }
                              sx={{
                                textTransform: 'none',
                                bgcolor: '#25D366',
                                color: '#fff',
                                '&:hover': { bgcolor: '#1ebe57' },
                                '&.Mui-disabled': { bgcolor: 'rgba(37, 211, 102, 0.35)', color: '#fff' },
                                minWidth: 110,
                              }}
                            >
                              {sending ? 'Sending…' : 'WhatsApp'}
                            </Button>
                            <Typography
                              variant="caption"
                              sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}
                            >
                              Engaged {row.engagedCount} time{row.engagedCount === 1 ? '' : 's'}
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ))}
        </Stack>
      </DialogContent>
    </Dialog>

    <Dialog
      open={!!soldUnitsDialogContext}
      onClose={() => setSoldUnitsDialogContext(null)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {soldUnitsDialogContext
              ? soldUnitsDialogContext.agentName
                ? `Sold Units - ${soldUnitsDialogContext.agentName} (${soldUnitsDialogContext.ceaNumber})`
                : `Sold Units - (${soldUnitsDialogContext.ceaNumber})`
              : 'Sold Units'}
          </Typography>
          <IconButton onClick={() => setSoldUnitsDialogContext(null)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        {soldUnitsLoading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : soldUnits.length === 0 ? (
          <Typography sx={{ py: 2, color: 'rgba(var(--color-text-rgb), 0.65)' }}>
            No sold units found.
          </Typography>
        ) : (
          <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Unit No</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Transaction No</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Option Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Price</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Agent role</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {soldUnits.map((unit) => (
                  <TableRow key={unit.transactionNo}>
                    <TableCell>{unit.unitNo}</TableCell>
                    <TableCell>{unit.transactionNo}</TableCell>
                    <TableCell>{unit.optionDate}</TableCell>
                    <TableCell align="right">
                      {unit.transactionPrice.toLocaleString('en-SG', { style: 'currency', currency: 'SGD', minimumFractionDigits: 0 })}
                    </TableCell>
                    <TableCell>{unit.propertyType}</TableCell>
                    <TableCell>{formatAgentRoleLabel(unit.agentRole)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>

    {/* User tab: sold unit details by project */}
    <Dialog
      open={!!userSoldUnitProject}
      onClose={() => setUserSoldUnitProject(null)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Sold Units — {userSoldUnitProject}
          </Typography>
          <IconButton onClick={() => setUserSoldUnitProject(null)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        {userSoldUnitDetailsLoading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : userSoldUnitDetails.length === 0 ? (
          <Typography sx={{ py: 2, color: 'rgba(var(--color-text-rgb), 0.65)' }}>
            No sold units found.
          </Typography>
        ) : (
          <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Unit No</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Transaction No</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Option Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Price</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Agent role</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userSoldUnitDetails.map((unit) => (
                  <TableRow key={unit.transactionNo}>
                    <TableCell>{unit.unitNo}</TableCell>
                    <TableCell>{unit.transactionNo}</TableCell>
                    <TableCell>{unit.optionDate}</TableCell>
                    <TableCell align="right">
                      {unit.transactionPrice.toLocaleString('en-SG', { style: 'currency', currency: 'SGD', minimumFractionDigits: 0 })}
                    </TableCell>
                    <TableCell>{formatAgentRoleLabel(unit.agentRole)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>

    {/* Success Closer: transaction details dialog */}
    <Dialog
      open={!!successCloserTxDialog}
      onClose={() => setSuccessCloserTxDialog(null)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              Transaction Details — {successCloserTxDialog?.agentName || successCloserTxDialog?.ceaRegNo}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
              CEA: {successCloserTxDialog?.ceaRegNo} | {successCloserTxDialog?.closingCount} closing(s)
            </Typography>
          </Box>
          <IconButton onClick={() => setSuccessCloserTxDialog(null)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        {successCloserTxDialog && successCloserTxDialog.transactions.length > 0 ? (
          <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Transaction No</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Project</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Activities</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Unit No</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Option Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Price</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Agent Role</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.85)' }}>Grouping</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {successCloserTxDialog.transactions.map((tx) => (
                  <TableRow key={`${tx.transactionNo}-${tx.agentCEANo}`}>
                    <TableCell>{tx.transactionNo}</TableCell>
                    <TableCell>{tx.reference || tx.projectCode}</TableCell>
                    <TableCell align="right">{formatCount(tx.projectActivityCount)}</TableCell>
                    <TableCell>{tx.unitNo}</TableCell>
                    <TableCell>{tx.optionDate}</TableCell>
                    <TableCell align="right">
                      {tx.transactionPrice.toLocaleString('en-SG', { style: 'currency', currency: 'SGD', minimumFractionDigits: 0 })}
                    </TableCell>
                    <TableCell>{tx.status}</TableCell>
                    <TableCell>{tx.saleType}</TableCell>
                    <TableCell>{formatAgentRoleLabel(tx.agentRole)}</TableCell>
                    <TableCell>{tx.grouping}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography sx={{ py: 2, color: 'rgba(var(--color-text-rgb), 0.65)' }}>
            No transaction details available.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  </>
  );
};

export default AnalyticsDashboardDialog;
